// Scene: low-poly moonlit valley with farm and cult compound.
// Mobile budget: no shadow maps, fog-limited draw distance, instanced props.

import * as THREE from 'three';

export const PLAY_RADIUS = 104;

/* ---------------- per-biome maps ----------------
 * Each land is a COMPLETELY different map: its own landform, its own water,
 * its own road ring, its own set-piece layout. The game reads LAKE/ROAD and
 * world.stoneAt/chapelAt live every frame, so switching maps is a matter of
 * mutating those in place (never reassign the objects — game.ts holds
 * references) and morphing the shared terrain/road geometry. */

export interface MapSpec {
  /** Base landform before the shared lake basin and mountain-ring wall. */
  field(x: number, z: number): number;
  lake: { x: number; z: number; r: number; level: number };
  road: { r: number; halfW: number };
  compoundAt: [number, number];
  chapelAt: [number, number];
  farms: [number, number][];
  /** The Donkey Farm: a named, fenced paddock whose herd is its own species.
   *  One land only — it is a place a pilot finds, not scenery every map gets. */
  donkeyFarm?: [number, number];
  pumpkins: [number, number][];
  graveyards: [number, number][];
  deadTrees: [number, number, number][]; // scale, x, z
  /** Crop beds. `d` is load-bearing: the tier-5 glyph is a 16-unit stamp, so a
   *  bed that cannot fill it fails `STAMP_MIN_STALKS` and the formation tops out
   *  at tier 4 instead. Every biome carries at least one bed deep enough — a bed
   *  too shallow is fine, a whole *land* without one is not, or THE SIGNAL would
   *  die the moment the tour left the valley. Gated in `corn.test.ts`. */
  cornFields: { cx: number; cz: number; w: number; d: number }[];
  trees: { count: number; seed: number };
  rocks: { count: number; seed: number };
  /** Transmission lines: each entry is one run of towers, and consecutive
   *  towers WITHIN a run carry a live span. Separate runs leave a gap in the
   *  line — the gate a player can cross without dropping anything. The game
   *  reads this to spawn the hazard (see game.ts pylons). */
  pylons?: [number, number][][];
  /** Where a tower once stood: the wreck that explains a gap in the line, and
   *  the landmark that tells a player from the air where the way through is. */
  gates?: [number, number][];
  /** Surface gravity, Earth = 1. Off-world lands hang everything the beam
   *  drops, which is the whole reason to go there. */
  gravity?: number;
  /** Metalled streets: a strip laid over the terrain, like the ring road. */
  streets?: { x1: number; z1: number; x2: number; z2: number; halfW: number }[];
  /** Terraced rows: `n` houses from (cx,cz), marching along `rot`. */
  terraces?: { cx: number; cz: number; rot: number; n: number }[];
  /** Drystone walls: instanced stone along each line. */
  walls?: [number, number, number, number][];
}

/** The tors that punch out of the moor's ridge line: x, z, height, spread. */
const MOOR_TORS: [number, number, number, number][] = [
  [-46, -52, 7.4, 13],
  [58, 36, 8.2, 15],
  [-8, 74, 6.6, 12],
  [76, -44, 7.0, 12],
  [-72, 14, 6.0, 11],
];

/**
 * A rounded hill: x, z, height, spread, and how much it is stretched along X.
 * The steepest slope a Gaussian of height h and spread s reaches is 0.606·h/s,
 * so h/s is the knob that decides whether the saucer's altitude tracking can
 * follow the ground up the face of it. Below ~1.4 it always can.
 */
type Hill = [number, number, number, number, number];

function hillsAt(hills: readonly Hill[], x: number, z: number): number {
  let sum = 0;
  for (const [hx, hz, height, spread, stretch] of hills) {
    const dx = (x - hx) / stretch;
    const dz = z - hz;
    sum += height * Math.exp(-(dx * dx + dz * dz) / (2 * spread * spread));
  }
  return sum;
}

/** Drumlins: the whaleback hills a glacier leaves, stretched along its flow. */
const VALLEY_DRUMLINS: readonly Hill[] = [
  [-54, -14, 5.6, 10, 1.9],
  [58, -6, 5.0, 9.5, 1.8],
  [14, 76, 6.2, 11, 1.7],
  [-32, -72, 4.8, 9, 2.0],
  [-70, 44, 5.4, 10, 1.6],
  [78, 52, 4.4, 9, 1.5],
];

/**
 * Quantise into benches `step` high, each climbing over its first 65% and then
 * running out flat. A hard `floor()` terrace is an infinitely steep wall: the
 * terrain mesh smears it over one 2.5-unit quad, but the saucer samples the
 * field continuously and would have its target altitude teleport up the face.
 * Spreading each riser over most of its bench keeps the bedding-plane look and
 * bounds the slope at (natural gradient / 0.65).
 */
function benched(v: number, step: number): number {
  const q = v / step;
  const bench = Math.floor(q);
  const climb = THREE.MathUtils.clamp((q - bench) / 0.65, 0, 1);
  return (bench + climb) * step;
}

/**
 * Flat-topped butte: a plateau of `height` out to `top`, then a scree ramp
 * `ramp` wide down to the desert floor, benched so the face reads as bedding
 * planes rather than a smooth cone.
 */
function butteAt(
  buttes: readonly [number, number, number, number, number][],
  x: number,
  z: number,
): number {
  let sum = 0;
  const steps = 3;
  for (const [bx, bz, height, top, ramp] of buttes) {
    const d = Math.hypot(x - bx, z - bz);
    const t = THREE.MathUtils.clamp((top + ramp - d) / ramp, 0, 1);
    sum += (height / steps) * Math.min(steps, benched(t * steps, 1));
  }
  return sum;
}

/** x, z, height, plateau radius, ramp width. */
const MESA_BUTTES: readonly [number, number, number, number, number][] = [
  [-66, 50, 10.5, 17, 13],
  [46, 64, 9.0, 14, 12],
  [24, -72, 9.6, 15, 12],
  [-78, -44, 8.2, 13, 11],
  [-6, -36, 7.4, 9, 10],
  [70, 8, 8.6, 12, 11],
];

/** The town's outskirts: rises the street grid never had to be laid across. */
const TOWN_RISES: readonly Hill[] = [
  // Kept well off the reservoir: a hill with an 18-unit spread still has half a
  // metre of shoulder thirty units away, and that was enough to fill it in.
  [-26, 86, 6.4, 18, 1.4],
  [72, 60, 5.2, 17, 1.2],
  [-82, -44, 6.0, 17, 1.4],
  [34, 88, 5.0, 16, 1.5],
];

/**
 * The Face on Mars, as ground rather than as a prop.
 *
 * Cydonia's mesa is a lump of rock that happens to catch the light like a
 * face, so that is how it is built: an elliptical plateau, then a brow, a
 * nose ridge and cheekbones added on top, and the eyes and mouth taken back
 * out of it. From directly above it is a mesa; from the low chase camera, with
 * the light raking across, it looks back at you.
 */
function faceOnMars(x: number, z: number): number {
  const fx = (x - FACE_AT[0]) / 17; // the head is taller than it is wide
  const fz = (z - FACE_AT[1]) / 21;
  const r = Math.hypot(fx, fz);
  if (r > 2.4) return 0;
  // The plateau: flat out to the cheeks, then a scree slope off the edge.
  let h = 9.4 * smoothstep(THREE.MathUtils.clamp((1.18 - r) / 0.45, 0, 1));
  const blob = (cx: number, cz: number, amp: number, sx: number, sz: number) => {
    const dx = (fx - cx) / sx;
    const dz = (fz - cz) / sz;
    return amp * Math.exp(-(dx * dx + dz * dz) / 2);
  };
  // Every feature is at least three terrain quads across, because the shared
  // sheet samples every 2.5 units and anything finer than that is not so much
  // subtle as absent. A face made of eight big lumps is the only kind this
  // terrain can hold.
  h += blob(0, -0.38, 2.0, 0.66, 0.24);   // brow ridge
  h += blob(0, 0.04, 2.6, 0.19, 0.42);    // nose
  h += blob(-0.66, 0.06, 1.2, 0.26, 0.34); // cheekbones
  h += blob(0.66, 0.06, 1.2, 0.26, 0.34);
  h -= blob(-0.38, -0.18, 4.0, 0.27, 0.19); // eye sockets, cut deep
  h -= blob(0.38, -0.18, 4.0, 0.27, 0.19);
  h -= blob(0, 0.52, 3.0, 0.44, 0.13);    // mouth
  h -= blob(0, 0.28, 1.1, 0.5, 0.1);      // the shadow under the nose
  return h;
}
const FACE_AT: [number, number] = [-24, -18];

/** Impact craters: x, z, rim radius, rim height, bowl depth. */
type Crater = [number, number, number, number, number];

function cratersAt(craters: readonly Crater[], x: number, z: number, peak = 0): number {
  let sum = 0;
  for (const [cx, cz, radius, rim, bowl] of craters) {
    const d = Math.hypot(x - cx, z - cz);
    if (d > radius * 2.2) continue;
    const wall = (d - radius) / (radius * 0.32);
    sum += rim * Math.exp(-wall * wall / 2);
    const inner = d / (radius * 0.66);
    sum -= bowl * Math.exp(-inner * inner / 2);
    // Central peak: the rebound spike a big impact leaves in the middle.
    if (peak > 0 && radius > 14) {
      const spike = d / (radius * 0.16);
      sum += peak * Math.exp(-spike * spike / 2);
    }
  }
  return sum;
}

const MARS_CRATERS: readonly Crater[] = [
  [40, -60, 17, 2.6, 4.2],
  [-24, 52, 13, 2.0, 3.2],
  [78, 26, 15, 2.4, 3.6],
];

const MOON_CRATERS: readonly Crater[] = [
  [-56, 30, 22, 3.4, 5.2],
  [46, 52, 16, 2.8, 4.0],
  [64, -34, 19, 3.0, 4.6],
  [-34, -62, 15, 2.6, 3.8],
  [-78, -14, 12, 2.2, 3.0],
  [12, 78, 13, 2.4, 3.4],
];

/** The fells standing over the frozen valley. */
const SNOW_FELLS: readonly Hill[] = [
  [-70, -20, 9.2, 19, 1.15],
  [58, -48, 8.0, 17, 1.3],
  [-26, 78, 7.2, 16, 1.4],
  [76, 34, 6.4, 14, 1.2],
];

export const MAPS: Record<BiomeId, MapSpec> = {
  // The original moonlit farmland: drumlins standing over rolling pasture, the
  // lake in the south-west. The gentlest land of the five — it is where the
  // game is learnt — but no longer a flat sheet.
  valley: {
    field: (x, z) => {
      const rolling =
        1.5 * Math.sin(x * 0.05) * Math.cos(z * 0.062) +
        0.8 * Math.sin(x * 0.11 + 1.7) * Math.sin(z * 0.09 + 0.6) +
        0.35 * Math.sin(x * 0.23 + 4.1) * Math.cos(z * 0.19 + 2.2);
      // Everything is BIASED ABOVE THE WATERLINE and the lake dug back out.
      // paintTerrain renders anything below -0.75 as lake bottom, so an
      // unbiased field of this amplitude paints a fifth of the pasture as
      // water — which is exactly what the valley used to do.
      const basin = 4.6 * Math.exp(-(((x + 8) ** 2 + (z + 30) ** 2)) / (2 * 12 * 12));
      return 2.3 + rolling * 0.95 - basin + hillsAt(VALLEY_DRUMLINS, x, z);
    },
    lake: { x: -8, z: -30, r: 13, level: -0.75 },
    road: { r: 62, halfW: 1.7 },
    compoundAt: [26, -20],
    chapelAt: [-6, 52],
    farms: [[-24, 12], [70, 44]],
    // Inside the ring road and close enough to the opening spawn that a first
    // flight finds it without being told, which is the whole point of it.
    donkeyFarm: [34, 28],
    pumpkins: [[-46, 22], [52, 66]],
    graveyards: [[42, -36]],
    deadTrees: [[1.15, 7.5, -27]],
    cornFields: [
      { cx: -38, cz: -8, w: 26, d: 18 },
      { cx: 4, cz: 38, w: 30, d: 17 },
      { cx: 40, cz: 10, w: 20, d: 16 },
      { cx: -30, cz: 34, w: 18, d: 14 },
      { cx: 34, cz: -38, w: 22, d: 14 },
      { cx: -14, cz: -58, w: 24, d: 15 },
      { cx: 58, cz: 34, w: 18, d: 14 },
      { cx: -58, cz: -30, w: 16, d: 13 },
      { cx: 76, cz: -14, w: 20, d: 15 },
      { cx: -78, cz: 12, w: 18, d: 14 },
      { cx: 12, cz: -80, w: 22, d: 15 },
      { cx: -34, cz: 74, w: 20, d: 14 },
    ],
    trees: { count: 175, seed: 1234567 },
    rocks: { count: 68, seed: 987654 },
  },
  // Stepped butte country: flat-topped plateaus rise past a tight dusty ring
  // road, an oasis pool inside it, boulders everywhere, barely a tree.
  mesa: {
    field: (x, z) => {
      const rolling =
        0.9 * Math.sin(x * 0.045 + 2.6) * Math.cos(z * 0.05 + 1.1) +
        0.45 * Math.sin(x * 0.13 + 0.4) * Math.sin(z * 0.1 + 3.0);
      // Butte field: a slow swell quantised into terraces — classic mesas.
      const swell =
        2.6 * Math.sin(x * 0.030 + 5.0) * Math.cos(z * 0.033 + 2.4) +
        1.2 * Math.sin(x * 0.058 + 1.9) * Math.cos(z * 0.049 + 0.7);
      const terraced = Math.max(0, benched(swell, 1.3));
      // Plateaus stay out of the central bowl so the ferry runs flow.
      const d = Math.hypot(x, z);
      const bowl = THREE.MathUtils.clamp((d - 34) / 26, 0, 1);
      // Tight: the oasis is an 8-unit pool, so its hollow has to come back up
      // inside 8 units or the shore is a wide grey stain of lake-bottom colour.
      const oasis = 4.6 * Math.exp(-(((x - 14) ** 2 + (z - 24) ** 2)) / (2 * 5.8 * 5.8));
      return 2.6 + rolling + terraced * bowl * 2.1
        + butteAt(MESA_BUTTES, x, z) * (0.35 + 0.65 * bowl) - oasis;
    },
    lake: { x: 14, z: 24, r: 8, level: -0.75 },
    road: { r: 52, halfW: 2.1 },
    compoundAt: [-30, 8],
    chapelAt: [58, -28],
    farms: [[-58, -18], [14, 58]],
    pumpkins: [[-12, -60]],
    graveyards: [[66, 18]],
    deadTrees: [[1.3, -20, -34], [1.1, 40, 32], [1.2, -52, 44], [0.9, 8, -18]],
    cornFields: [
      { cx: -40, cz: -6, w: 20, d: 14 },
      { cx: 6, cz: -36, w: 24, d: 18 },   // the stamp field
      { cx: -16, cz: 40, w: 18, d: 13 },
      { cx: 38, cz: 6, w: 16, d: 12 },
      { cx: 70, cz: -26, w: 16, d: 12 },
      { cx: -64, cz: 28, w: 16, d: 12 },
      { cx: -6, cz: -74, w: 18, d: 12 },
      { cx: 26, cz: 68, w: 16, d: 12 },
    ],
    trees: { count: 34, seed: 445566 },
    rocks: { count: 170, seed: 13579 },
  },
  // Deep-winter bowl: long soft drifts, a big frozen lake dead centre, a wide
  // outer road, pine forest crowding the slopes.
  snow: {
    field: (x, z) => {
      const swell =
        2.4 * Math.sin(x * 0.032 + 1.2) * Math.cos(z * 0.028 + 4.4) +
        1.1 * Math.sin(x * 0.07 + 0.8) * Math.sin(z * 0.06 + 2.2) +
        0.3 * Math.sin(x * 0.18) * Math.cos(z * 0.16 + 1.3);
      // Wind-scoured drifts, all combed the same way across the whole bowl.
      const drift = 0.62 * Math.sin((x + z) * 0.13 + 0.9) + 0.3 * Math.sin((x - z) * 0.21);
      // The frozen lake sits in its own dug basin (see the valley's note).
      const basin = 5.4 * Math.exp(-(((x - 18) ** 2 + (z + 12) ** 2)) / (2 * 15 * 15));
      return 3.1 + swell * 0.85 + drift - basin + hillsAt(SNOW_FELLS, x, z);
    },
    lake: { x: 18, z: -12, r: 17, level: -0.75 },
    road: { r: 70, halfW: 1.9 },
    compoundAt: [-44, -40],
    chapelAt: [50, 30],
    farms: [[-62, 14], [24, -56]],
    pumpkins: [],
    graveyards: [[-20, 60]],
    deadTrees: [[1.1, 30, 18], [1.0, -28, 36]],
    cornFields: [
      { cx: -36, cz: -14, w: 20, d: 13 },
      { cx: 4, cz: 34, w: 24, d: 18 },    // the stamp field
      { cx: 48, cz: -8, w: 16, d: 12 },
      { cx: -8, cz: -52, w: 18, d: 12 },
    ],
    trees: { count: 240, seed: 778899 },
    rocks: { count: 26, seed: 24680 },
  },
  // High moor: the land with real relief. A ridge system with five tors
  // standing out of it, drystone walls running over the tops, a peat loch and
  // a single-track lane. The centre is graded calmer so the ferry run to the
  // stone is flyable — everywhere else you are crossing hills.
  moor: {
    field: (x, z) => {
      const ridges =
        3.9 * Math.sin(x * 0.026 + 0.6) * Math.cos(z * 0.023 + 2.1) +
        2.0 * Math.sin(x * 0.052 + 2.4) * Math.sin(z * 0.045 + 1.2) +
        0.55 * Math.sin(x * 0.15) * Math.cos(z * 0.13 + 0.5);
      let tors = 0;
      for (const [tx, tz, height, spread] of MOOR_TORS) {
        const dx = x - tx;
        const dz = z - tz;
        tors += height * Math.exp(-(dx * dx + dz * dz) / (2 * spread * spread));
      }
      const calm = THREE.MathUtils.clamp((Math.hypot(x, z) - 18) / 22, 0.3, 1);
      // The ridge system is BIASED ABOVE THE WATERLINE and the loch dug back
      // out where the water actually is. Left unbiased, a landform with this
      // much amplitude puts a fifth of the map under -0.75, and paintTerrain
      // renders everything below that as lake bottom — which read as a black
      // hole in the middle of the moor rather than as a valley floor.
      const loch = 5.0 * Math.exp(-(((x - 26) ** 2 + (z - 34) ** 2)) / (2 * 14 * 14));
      return (3.3 + ridges * 0.55 - loch) * calm + tors * calm;
    },
    lake: { x: 26, z: 34, r: 12, level: -0.75 },
    road: { r: 66, halfW: 1.5 },
    compoundAt: [-16, -20],
    chapelAt: [34, -52],
    farms: [[-52, 28], [28, 6]],
    pumpkins: [],
    graveyards: [[-58, -22]],
    deadTrees: [[1.25, -34, 8], [1.0, 10, 46], [1.15, 54, -14], [0.95, -20, 60]],
    cornFields: [
      { cx: -34, cz: 4, w: 22, d: 18 },   // the stamp field
      { cx: 8, cz: 24, w: 18, d: 12 },
      { cx: 44, cz: -30, w: 16, d: 12 },
      { cx: -12, cz: -46, w: 18, d: 12 },
      { cx: -66, cz: 48, w: 16, d: 11 },
    ],
    trees: { count: 46, seed: 553311 },
    rocks: { count: 210, seed: 60912 },
    walls: [
      [-88, 8, -30, 18], [-30, 18, 14, 30], [14, 30, 56, 20],
      [-24, -66, -18, -8], [-18, -8, -6, 36],
      [40, -8, 78, -22], [46, 56, 88, 40],
      [-72, -30, -34, -44],
    ],
  },
  // The fiat town: a graded valley floor with a street grid, brick terraces,
  // allotments where the corn used to be, a reservoir, and a transmission line
  // strung straight across the flight path.
  town: {
    field: (x, z) => {
      const base =
        1.1 * Math.sin(x * 0.024 + 1.4) * Math.cos(z * 0.021 + 0.3) +
        0.5 * Math.sin(x * 0.06 + 2.2) * Math.sin(z * 0.055 + 1.9);
      // One hill on the edge of town, because every town has one.
      const dx = x - 66;
      const dz = z + 56;
      const hill = 5.6 * Math.exp(-(dx * dx + dz * dz) / (2 * 26 * 26));
      // The built-up middle is graded flat: you cannot lay a street on a wave.
      // The rises are graded out too, so they belong to the outskirts and the
      // terraces still stand on the level.
      const grade = THREE.MathUtils.clamp((Math.hypot(x, z) - 30) / 26, 0.16, 1);
      // The reservoir gets its own hollow, so it stays water whatever the
      // outskirts do around it.
      const reservoir = 3.4 * Math.exp(-(((x + 66) ** 2 + (z - 56) ** 2)) / (2 * 9.5 * 9.5));
      return base * grade + hill + hillsAt(TOWN_RISES, x, z) * grade - reservoir;
    },
    lake: { x: -66, z: 56, r: 11, level: -0.75 },
    road: { r: 58, halfW: 2.4 },
    compoundAt: [26, -30],
    chapelAt: [-30, -44],
    farms: [[-62, -24], [14, 58]],
    pumpkins: [],
    graveyards: [[-50, -4]],
    deadTrees: [[1.05, 42, 46]],
    cornFields: [
      { cx: -60, cz: 16, w: 12, d: 10 },
      { cx: 42, cz: 12, w: 12, d: 10 },
      { cx: -8, cz: 44, w: 20, d: 18 },   // the stamp field
      { cx: 8, cz: -50, w: 12, d: 10 },
      { cx: 60, cz: -8, w: 12, d: 10 },
      { cx: -74, cz: -16, w: 12, d: 10 },
    ],
    trees: { count: 78, seed: 771122 },
    rocks: { count: 18, seed: 332211 },
    streets: [
      { x1: -22, z1: -42, x2: -22, z2: 40, halfW: 2.5 },
      { x1: 22, z1: -42, x2: 22, z2: 40, halfW: 2.5 },
      { x1: -44, z1: -18, x2: 44, z2: -18, halfW: 2.5 },
      { x1: -44, z1: 4, x2: 44, z2: 4, halfW: 2.5 },
      { x1: -44, z1: 26, x2: 44, z2: 26, halfW: 2.5 },
    ],
    // `rot` is the row's heading; the front doors face (sin, -cos) of it, so a
    // row is turned to put its faces on the street it belongs to. rot 0 faces
    // -Z, pi faces +Z, pi/2 faces +X, -pi/2 faces -X.
    terraces: [
      { cx: -26.4, cz: -23.5, rot: Math.PI, n: 5 },
      { cx: 17.4, cz: -23.5, rot: Math.PI, n: 4 },
      { cx: -40, cz: -12.5, rot: 0, n: 5 },
      { cx: 4, cz: -12.5, rot: 0, n: 4 },
      { cx: -40, cz: 9.5, rot: 0, n: 5 },
      { cx: 4, cz: 9.5, rot: 0, n: 3 },
      { cx: -26.4, cz: 20.5, rot: Math.PI, n: 5 },
      { cx: 17.4, cz: 20.5, rot: Math.PI, n: 4 },
      { cx: -27.5, cz: -32, rot: Math.PI / 2, n: 3 },
      { cx: 27.5, cz: 36.8, rot: -Math.PI / 2, n: 3 },
    ],
    // Two short live runs occupy the back-garden utility corridor between the
    // z=-18 and z=4 streets. The old seven-tower line read as a web laid over
    // the whole town; these four towers keep GRID REBELLION and real collision
    // counterplay without making every ordinary flight a pylon level. Put the
    // nearer eastern run first so the local-signal compass names a visible
    // target, while the broad wreck-marked centre gap remains an obvious route.
    pylons: [
      [[28, -1], [50, -1]],
      [[-90, -1], [-64, -1]],
    ],
    gates: [[-17, -2]],
  },
  // Cydonia. Dust plains under a rust sky, the Face looking up out of the
  // south-west, a shield volcano in the north-east, and craters. Everything is
  // biased well above the waterline so the crater bowls are floor, not water —
  // there is no standing water on Mars but the brine seep.
  mars: {
    field: (x, z) => {
      const dunes =
        1.5 * Math.sin(x * 0.038 + 1.1) * Math.cos(z * 0.034 + 2.7) +
        0.7 * Math.sin(x * 0.09 + 0.5) * Math.sin(z * 0.08 + 1.4) +
        0.3 * Math.sin((x + z) * 0.17);
      // Olympus: a shield volcano, wide and shallow, with a caldera dropped in.
      const vd = Math.hypot(x - 62, z - 42);
      const shield = 11 * Math.exp(-(vd * vd) / (2 * 26 * 26));
      const caldera = 6.4 * Math.exp(-(vd * vd) / (2 * 7 * 7));
      const seep = 6.2 * Math.exp(-(((x - 12) ** 2 + (z - 38) ** 2)) / (2 * 8.5 * 8.5));
      return 4.2 + dunes + shield - caldera + faceOnMars(x, z)
        + cratersAt(MARS_CRATERS, x, z) - seep;
    },
    lake: { x: 12, z: 38, r: 9, level: -0.75 },
    road: { r: 60, halfW: 2.2 },
    compoundAt: [22, -22],
    chapelAt: [-30, 14],
    farms: [[-64, 60], [50, -6]],
    pumpkins: [],
    graveyards: [[-8, -70]],
    deadTrees: [],
    cornFields: [
      { cx: 2, cz: -38, w: 22, d: 18 },   // the stamp field
      { cx: 6, cz: 24, w: 18, d: 12 },
      { cx: 34, cz: 66, w: 16, d: 12 },
      { cx: -70, cz: 4, w: 16, d: 12 },
      { cx: 14, cz: -46, w: 16, d: 12 },
    ],
    trees: { count: 0, seed: 424242 },
    rocks: { count: 240, seed: 909090 },
    gravity: 0.38,
  },
  // The near side. Mare and highlands, craters with central peaks, and a sea of
  // impact glass. The lowest gravity in the tour: what the beam drops, hangs.
  moon: {
    field: (x, z) => {
      const mare =
        1.1 * Math.sin(x * 0.022 + 0.4) * Math.cos(z * 0.02 + 1.9) +
        0.45 * Math.sin(x * 0.055 + 2.2) * Math.sin(z * 0.05 + 0.8);
      // The highlands: older, rougher ground away from the mare basin.
      const uplandT = THREE.MathUtils.clamp((Math.hypot(x, z) - 30) / 40, 0, 1);
      const upland = uplandT * (2.6 * Math.sin(x * 0.031 + 2.0) * Math.cos(z * 0.029 + 4.1) + 2.2);
      const glass = 6.0 * Math.exp(-(((x + 20) ** 2 + (z - 18) ** 2)) / (2 * 11.5 * 11.5));
      return 4.6 + mare + upland + cratersAt(MOON_CRATERS, x, z, 2.2) - glass;
    },
    lake: { x: -20, z: 18, r: 11, level: -0.75 },
    road: { r: 68, halfW: 2.0 },
    compoundAt: [28, 16],
    chapelAt: [-40, -34],
    farms: [[62, -50], [-70, 46]],
    pumpkins: [],
    graveyards: [[24, -74]],
    deadTrees: [],
    cornFields: [
      { cx: -6, cz: -22, w: 22, d: 18 },  // the stamp field
      { cx: 40, cz: -12, w: 16, d: 12 },
      { cx: -52, cz: 66, w: 16, d: 12 },
      { cx: 4, cz: 44, w: 16, d: 12 },
    ],
    trees: { count: 0, seed: 121212 },
    rocks: { count: 190, seed: 565656 },
    gravity: 0.17,
  },
};

/** The ACTIVE map's water body. Mutated in place on a map switch — game.ts
 *  holds a reference and reads it live every frame, so never reassign. */
export const LAKE = { x: -8, z: -30, r: 13, level: -0.75 };

/** Ring road the cattle convoys drive; a dirt loop around mid-valley. */
/** The ACTIVE map's ring road. Mutated in place on a map switch, like LAKE. */
export const ROAD = { r: 62, halfW: 1.7 };

let activeMap: MapSpec = MAPS.valley;

/** Point LAKE/ROAD/heightAt at a biome's map. Geometry morphs separately. */
function activateMapData(id: BiomeId): void {
  activeMap = MAPS[id];
  Object.assign(LAKE, activeMap.lake);
  Object.assign(ROAD, activeMap.road);
}

export function onRoad(x: number, z: number, margin = 0.5): boolean {
  return Math.abs(Math.hypot(x, z) - ROAD.r) < ROAD.halfW + margin;
}

/* ---------------- biomes ---------------- */

export type BiomeId = 'valley' | 'mesa' | 'snow' | 'moor' | 'town' | 'mars' | 'moon';

export interface BiomePalette {
  label: string;
  fog: string;
  hemiSky: string;
  hemiGround: string;
  moonlight: string;
  moon: string;
  halo: string;
  low: string;      // terrain grass/floor
  high: string;
  rock: string;     // terrain above the rock line
  rockHi: string;
  /** Height at which ground stops being grass/sand/snow and starts being bare
   *  rock. Lands whose own hills are meant to stay green set this above them,
   *  so only the crater wall goes grey; lands whose hills ARE rock (tors,
   *  buttes) set it low enough to catch their tops. */
  rockLine: number;
  deep: string;     // below the waterline
  canopy: string;
  corn: string;
  rockProp: string; // scattered boulders
  water: string;
  waterEmissive: string;
  waterOpacity: number;
  waterShine: number;
  waterAmp: number;
  road: string;
  mistTint: string;
  precip: 'rain' | 'snow';
  precipColor: string;
}

export const BIOMES: Record<BiomeId, BiomePalette> = {
  valley: {
    label: 'THE MOONLIT VALLEY',
    rockLine: 11,
    fog: '#141030',
    hemiSky: '#6b7cc4', hemiGround: '#26382a', moonlight: '#a8bcf5',
    moon: '#ffb347', halo: '#ffffff',
    low: '#28543f', high: '#4a7a4e', rock: '#4a567c', rockHi: '#5c6b96', deep: '#1c2a3a',
    canopy: '#2b5a41', corn: '#b3a04a', rockProp: '#4c5570',
    water: '#1a3050', waterEmissive: '#0a1626', waterOpacity: 0.9, waterShine: 220, waterAmp: 0.05,
    road: '#41372a', mistTint: '#ffffff',
    precip: 'rain', precipColor: '#9fb4dd',
  },
  mesa: {
    label: 'THE DESERT MESA',
    rockLine: 5.5,
    fog: '#241016',
    hemiSky: '#9c6a4c', hemiGround: '#3c241a', moonlight: '#e0b090',
    moon: '#ffc98a', halo: '#ffd9a8',
    low: '#7a4a2c', high: '#a06838', rock: '#8c4632', rockHi: '#b06a44', deep: '#33201a',
    canopy: '#6a6c34', corn: '#c98e42', rockProp: '#8a4a34',
    water: '#20544c', waterEmissive: '#0c221e', waterOpacity: 0.9, waterShine: 220, waterAmp: 0.05,
    road: '#57381f', mistTint: '#d9a878',
    precip: 'rain', precipColor: '#c9ab8e',
  },
  snow: {
    label: 'THE FROZEN VALLEY',
    rockLine: 10,
    fog: '#1a2438',
    hemiSky: '#92a8d0', hemiGround: '#46536a', moonlight: '#cfe0ff',
    moon: '#e8f0ff', halo: '#cfe0ff',
    low: '#b8c6d6', high: '#e6eef6', rock: '#8494b4', rockHi: '#a8b8d4', deep: '#5a728c',
    canopy: '#cfdee2', corn: '#d8cfa8', rockProp: '#94a2ba',
    water: '#9cbcd6', waterEmissive: '#2c4258', waterOpacity: 0.96, waterShine: 40, waterAmp: 0.012,
    road: '#5c6674', mistTint: '#dfe8f2',
    precip: 'snow', precipColor: '#f0f6ff',
  },
  moor: {
    label: 'THE HIGH MOOR',
    rockLine: 6,
    fog: '#161d24',
    hemiSky: '#8894a6', hemiGround: '#4a4436', moonlight: '#ccd8e6',
    moon: '#e6dcc4', halo: '#d4dce6',
    low: '#5e5442', high: '#83765a', rock: '#6a7076', rockHi: '#8a9096', deep: '#1c2628',
    canopy: '#46583c', corn: '#b4a65c', rockProp: '#787e84',
    water: '#223032', waterEmissive: '#0c1618', waterOpacity: 0.94, waterShine: 90, waterAmp: 0.035,
    road: '#565749', mistTint: '#c8d2dc',
    precip: 'rain', precipColor: '#b4c2d4',
  },
  town: {
    // Sodium light pollution washes the whole sky orange — the one land where
    // the moon is not the brightest thing in it.
    label: 'THE FIAT TOWN',
    rockLine: 10,
    fog: '#1d1a20',
    hemiSky: '#8c7a72', hemiGround: '#2f2f31', moonlight: '#dcc9ae',
    moon: '#ffcf8a', halo: '#ffd9a0',
    low: '#414c3c', high: '#586448', rock: '#5e6066', rockHi: '#7a7c82', deep: '#1e262c',
    canopy: '#3a4e34', corn: '#a8b858', rockProp: '#6e6e74',
    water: '#1c2c38', waterEmissive: '#0a1620', waterOpacity: 0.92, waterShine: 180, waterAmp: 0.04,
    road: '#3c3d42', mistTint: '#e6c8a4',
    precip: 'rain', precipColor: '#c8cad4',
  },
  mars: {
    // Cydonia at night: everything is iron oxide, the sky included, and the
    // two moons are too small to light anything. Phobos stands in for the moon.
    label: 'THE RED PLANET',
    rockLine: 9,
    fog: '#2a1410',
    hemiSky: '#a86048', hemiGround: '#4a2418', moonlight: '#f0b090',
    moon: '#d8d0c4', halo: '#e8c0a0',
    low: '#8c4a2c', high: '#b06a3c', rock: '#7a3a26', rockHi: '#a85a38', deep: '#3a1c14',
    canopy: '#6a4030', corn: '#9aa860', rockProp: '#8a4028',
    water: '#4a2a24', waterEmissive: '#1a0e0a', waterOpacity: 0.95, waterShine: 60, waterAmp: 0.02,
    road: '#5e3220', mistTint: '#e0a882',
    precip: 'rain', precipColor: '#c08a68',
  },
  moon: {
    // No air, so no fog to speak of and no colour in the shadows: the near side
    // is a grey world lit hard by an Earth that is the brightest thing in it.
    label: 'THE NEAR SIDE',
    rockLine: 11,
    fog: '#0a0b10',
    hemiSky: '#8890a0', hemiGround: '#3a3d44', moonlight: '#e8eef8',
    moon: '#7ba7d8', halo: '#a8c8f0',
    low: '#8a8c92', high: '#b4b6bc', rock: '#6e7076', rockHi: '#9a9ca4', deep: '#2c2e34',
    canopy: '#7a7c84', corn: '#b8bcc4', rockProp: '#82848c',
    water: '#2a2e38', waterEmissive: '#12161e', waterOpacity: 0.97, waterShine: 320, waterAmp: 0.004,
    road: '#5a5c62', mistTint: '#c8d0dc',
    precip: 'snow', precipColor: '#e8eef8',
  },
};

/** The tour order: complete a council and the saucer moves on. Seven lands now,
 *  so reaching every land takes six departures. */
const TOUR: readonly BiomeId[] = ['valley', 'mesa', 'snow', 'moor', 'town', 'mars', 'moon'];

export function nextBiome(id: BiomeId): BiomeId {
  const at = TOUR.indexOf(id);
  return TOUR[(at + 1) % TOUR.length];
}

export interface World {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGLRenderer;
  heightAt(x: number, z: number): number;
  bonfires: THREE.Vector3[];
  flames: THREE.Sprite[];
  fireLight: THREE.PointLight;
  corn: CornSystem;
  mist: THREE.Sprite[];
  bats: THREE.Mesh[];
  stoneAt: THREE.Vector3;
  chapelAt: THREE.Vector3;
  stoneRing: THREE.Mesh;
  embers: THREE.Sprite[];
  moonGlints: THREE.Mesh[];
  water: THREE.Mesh;
  waterBase: Float32Array;
  rain: THREE.Points;
  hemi: THREE.HemisphereLight;
  sun: THREE.DirectionalLight;
  moonMat: THREE.MeshBasicMaterial;
  moonHaloMat: THREE.SpriteMaterial;
  // Biome state — repainted per run, read live by the game's ambience.
  biome: BiomeId;
  biomeLabel: string;
  waterAmp: number;
  precip: 'rain' | 'snow';
  fogColor: THREE.Color;
  moonColor: THREE.Color;
  haloColor: THREE.Color;
  hemiSkyColor: THREE.Color;
  /** Static scatter is instanced, so gameplay cannot inspect individual tree
   *  meshes. This query keeps dynamic shacks off authored trees and rocks. */
  propClearAt(x: number, z: number, radius: number): boolean;
  applyBiome(id: BiomeId): void;
  /** Build a land's set pieces ahead of time without switching to it, so the
   *  cost lands somewhere harmless instead of on the departure frame. */
  warmBiome(id: BiomeId): void;
}

/** Instanced cornfields. The tractor beam flattens stalks into swirls —
 *  fly with the beam held to draw crop circles. */
export interface CornSnapshot {
  /** x, z and state triplets: 0 standing, 1 laid, 2 laid as tier-5 glyph ink.
   * A typed snapshot keeps the one-off report image compact without leaking the
   * mutable stalk records. Ink is separated from ordinary flattening so the
   * signature can be drawn in its own colour — everything the beam touches is
   * flattened, so without this the mark is the same ink as the rings it sits
   * inside and stops reading as a signature at all. */
  points: Float32Array;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  down: number;
  knocked: number;
}

/** How a stencil meets the crop. Defaults reproduce the pre-glyph behaviour. */
export interface StencilOpts {
  /** Swap ink and ground: the mark is flattened into a field left standing.
   *  A glyph reads this way round and only this way round — raised strokes on
   *  a laid bed are a bright smudge from the reveal vantage, because standing
   *  corn scatters and flattened corn holds a hard edge. */
  inverted?: boolean;
  /** Mask resolution. A letterform needs finer sampling than a swirl. */
  size?: number;
  /** Heading in radians for flattened ink, used only when `inverted`. */
  heading?: number;
}

export class CornSystem {
  readonly mesh: THREE.InstancedMesh;
  // `flat` is a LOCK, not an orientation: once set, the stalk is skipped by
  // every further flatten pass, which is how a standing motif survives a later
  // sweep of the beam. `down` is the orientation. They agree everywhere except
  // on a raised motif stalk — locked, but standing — and conflating the two
  // made `flattened` count raised stalks as flattened, so the harvest report's
  // "N stalks of crop art" over-reported every stencil.
  // `ink` marks a stalk laid as part of a tier-5 glyph rather than by the beam
  // or an earlier tier, so the harvest card can colour the signature apart from
  // the formation around it.
  private stalks: { x: number; z: number; y: number; flat: boolean; down: boolean; ink: boolean }[] = [];
  flattened = 0;
  /** First-time knockdowns this bed has seen. Unlike `flattened`, this never
   *  falls when a stencil raises a stalk back into the finished artwork. */
  knocked = 0;
  private dirty = false;

  constructor(fields: { cx: number; cz: number; w: number; d: number }[]) {
    for (const f of fields) {
      const density = 1.55;
      const nx = Math.floor(f.w * density);
      const nz = Math.floor(f.d * density);
      for (let ix = 0; ix < nx; ix++) {
        for (let iz = 0; iz < nz; iz++) {
          const x = f.cx - f.w / 2 + (ix + 0.18 + Math.random() * 0.64) / density;
          const z = f.cz - f.d / 2 + (iz + 0.18 + Math.random() * 0.64) / density;
          if (onRoad(x, z, 0.6)) continue; // the road cuts a clean lane through
          this.stalks.push({ x, z, y: heightAt(x, z), flat: false, down: false, ink: false });
        }
      }
    }
    const geo = new THREE.ConeGeometry(0.13, 1.7, 5);
    geo.translate(0, 0.85, 0);
    const mat = new THREE.MeshLambertMaterial({ color: '#ffffff', flatShading: true });
    this.mesh = new THREE.InstancedMesh(geo, mat, this.stalks.length);
    this.mesh.name = 'corn';
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3();
    const col = new THREE.Color();
    this.stalks.forEach((st, i) => {
      const scale = 0.85 + Math.random() * 0.35;
      s.set(1, scale, 1);
      q.setFromAxisAngle(UP, Math.random() * Math.PI * 2);
      m.compose(new THREE.Vector3(st.x, st.y, st.z), q, s);
      this.mesh.setMatrixAt(i, m);
      col.set('#b3a04a').offsetHSL((Math.random() - 0.5) * 0.02, 0, (Math.random() - 0.5) * 0.08);
      this.mesh.setColorAt(i, col);
    });
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  }

  /** Lay one stalk tangentially around (cx,cz); dir flips the swirl. */
  private layStalk(i: number, cx: number, cz: number, dir: number): void {
    const st = this.stalks[i];
    const tangent = Math.atan2(st.z - cz, st.x - cx) + (Math.PI / 2) * dir;
    this.layStalkDir(i, tangent);
  }

  /**
   * Lay one stalk along a fixed heading rather than around a centre. A swirl
   * reads as a circle from above, which is what tiers 2 to 4 want and exactly
   * what a glyph does not: combed one way, the flattened strokes hold an edge
   * and the letterform survives the read from altitude.
   */
  layStalkDir(i: number, heading: number, ink = false): void {
    const st = this.stalks[i];
    st.flat = true;
    if (ink) st.ink = true;
    if (!st.down) { st.down = true; this.flattened++; this.knocked++; }
    this.dirty = true;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const qz = new THREE.Quaternion();
    q.setFromAxisAngle(UP, -heading);
    qz.setFromAxisAngle(FORWARD, 1.45);
    q.multiply(qz);
    m.compose(new THREE.Vector3(st.x, st.y + 0.08, st.z), q, new THREE.Vector3(1, 1, 1));
    this.mesh.setMatrixAt(i, m);
  }

  /** Flatten stalks within r of (x,z), swirling tangentially. Returns count. */
  flattenAt(x: number, z: number, r: number, dir = 1): number {
    const r2 = r * r;
    let count = 0;
    for (let i = 0; i < this.stalks.length; i++) {
      const st = this.stalks[i];
      if (st.flat) continue;
      const dx = st.x - x;
      const dz = st.z - z;
      if (dx * dx + dz * dz > r2) continue;
      this.layStalk(i, x, z, dir);
      count++;
    }
    if (count > 0) this.mesh.instanceMatrix.needsUpdate = true;
    return count;
  }

  /** Flatten an annulus band r0..r1 — the crisp ring of a real formation. */
  flattenBand(cx: number, cz: number, r0: number, r1: number, dir: number): number {
    const r0sq = r0 * r0;
    const r1sq = r1 * r1;
    let count = 0;
    for (let i = 0; i < this.stalks.length; i++) {
      const st = this.stalks[i];
      if (st.flat) continue;
      const dx = st.x - cx;
      const dz = st.z - cz;
      const d2 = dx * dx + dz * dz;
      if (d2 < r0sq || d2 > r1sq) continue;
      this.layStalk(i, cx, cz, dir);
      count++;
    }
    if (count > 0) this.mesh.instanceMatrix.needsUpdate = true;
    return count;
  }

  /** Flatten n radial corridors from r0 to r1 — formation spokes. */
  flattenSpokes(cx: number, cz: number, n: number, r0: number, r1: number, halfWidth: number, dir: number, angleOffset = 0): number {
    let count = 0;
    for (let i = 0; i < this.stalks.length; i++) {
      const st = this.stalks[i];
      if (st.flat) continue;
      const dx = st.x - cx;
      const dz = st.z - cz;
      const d = Math.hypot(dx, dz);
      if (d < r0 || d > r1) continue;
      const a = Math.atan2(dz, dx) - angleOffset;
      const sector = Math.PI * 2 / n;
      const off = ((a % sector) + sector) % sector;
      const arc = Math.min(off, sector - off) * d; // distance from spoke centreline
      if (arc > halfWidth) continue;
      this.layStalk(i, cx, cz, dir);
      count++;
    }
    if (count > 0) this.mesh.instanceMatrix.needsUpdate = true;
    return count;
  }

  /** Stand a stalk back up and shield it from future flattening (flat=true
   *  + standing matrix): standing-crop motifs against a flattened bed, the
   *  way real pictogram formations read. */
  private raiseStalk(i: number): void {
    const st = this.stalks[i];
    st.flat = true;                                       // lock: never re-flattened
    if (st.down) { st.down = false; this.flattened--; }    // but it is STANDING
    st.ink = false;                                        // and it is no longer a mark
    this.dirty = true;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    q.setFromAxisAngle(UP, (i * 2.4) % (Math.PI * 2));
    m.compose(new THREE.Vector3(st.x, st.y, st.z), q, new THREE.Vector3(1, 1.05, 1));
    this.mesh.setMatrixAt(i, m);
  }

  /**
   * Stencil stamp: draw() paints white-on-black; within `radius`, painted
   * pixels leave stalks STANDING (the motif), unpainted ones get flattened.
   * Under `inverted` the two swap, so the paint is the flattened glyph and the
   * field around it stays up.
   */
  stampStencil(
    cx: number, cz: number, radius: number,
    draw: (g: CanvasRenderingContext2D, size: number) => void,
    opts: StencilOpts = {},
  ): number {
    const size = opts.size ?? 128;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const g = canvas.getContext('2d', { willReadFrequently: true })!;
    g.fillStyle = '#000';
    g.fillRect(0, 0, size, size);
    g.fillStyle = '#fff';
    draw(g, size);
    const pixels = g.getImageData(0, 0, size, size).data;
    return this.stampMask(cx, cz, radius, size, (px, pz) => pixels[(pz * size + px) * 4] > 127, opts);
  }

  /**
   * The stencil's geometry half, split out from the canvas so it can be driven
   * by any mask (and unit-tested without a DOM). `painted(px,pz)` samples a
   * size x size grid laid over the stamp's bounding square.
   */
  stampMask(
    cx: number, cz: number, radius: number, size: number,
    painted: (px: number, pz: number) => boolean,
    opts: StencilOpts = {},
  ): number {
    let count = 0;
    const r2 = radius * radius;
    const heading = opts.heading ?? 0;
    for (let i = 0; i < this.stalks.length; i++) {
      const st = this.stalks[i];
      const dx = st.x - cx;
      const dz = st.z - cz;
      if (dx * dx + dz * dz > r2) continue;
      const px = Math.round(size / 2 + (dx / radius) * (size / 2 - 2));
      const pz = Math.round(size / 2 + (dz / radius) * (size / 2 - 2));
      // `ink` is "this stalk belongs to the drawn mark", whichever way round the
      // mark is made. Inverted lays the ink and stands the ground; upright
      // raises the ink and lays the ground.
      const ink = painted(px, pz);
      const lay = opts.inverted ? ink : !ink;
      if (!lay) {
        this.raiseStalk(i);
        count++;
      } else if (opts.inverted) {
        // Deliberately ignores the flat lock. The beam and the earlier tiers
        // have already flattened part of the glyph's own footprint, and those
        // stalks still belong to the mark: skipping them would punch holes in
        // the signature's colour and leave them swirled against its grain.
        // `knocked` cannot double-count, since it only counts a stalk going
        // down for the first time.
        this.layStalkDir(i, heading, true);
        count++;
      } else if (!st.flat) {
        this.layStalk(i, cx, cz, 1);
        count++;
      }
    }
    if (count > 0) this.mesh.instanceMatrix.needsUpdate = true;
    return count;
  }

  /**
   * How many stalks, standing or laid, fall inside a stamp of this radius.
   * The tier-5 gate: a bed that cannot fill the footprint would render the
   * glyph clipped, so the formation stops at tier 4 rather than stamping a
   * letterform with its side sheared off. Counts laid stalks too, so a player
   * cannot disqualify their own field by mowing it first.
   */
  stalksWithin(cx: number, cz: number, radius: number): number {
    const r2 = radius * radius;
    let count = 0;
    for (const st of this.stalks) {
      const dx = st.x - cx;
      const dz = st.z - cz;
      if (dx * dx + dz * dz <= r2) count++;
    }
    return count;
  }

  /** Satellite circles around the centre — the classic grand formation. */
  flattenSatellites(cx: number, cz: number, n: number, dist: number, r: number, dir: number, angleOffset = 0): number {
    let count = 0;
    for (let k = 0; k < n; k++) {
      const a = angleOffset + (k / n) * Math.PI * 2;
      count += this.flattenAt(cx + Math.cos(a) * dist, cz + Math.sin(a) * dist, r, dir);
    }
    return count;
  }

  reset(): void {
    // Guard on `dirty`, not on `flattened`: a formation made only of raised
    // motif stalks leaves flattened at 0 while every matrix is still displaced.
    if (!this.dirty) return;
    this.flattened = 0;
    this.knocked = 0;
    this.dirty = false;
    const m = new THREE.Matrix4();
    const q = new THREE.Quaternion();
    const s = new THREE.Vector3();
    this.stalks.forEach((st, i) => {
      st.flat = false;
      st.down = false;
      st.ink = false;
      const scale = 0.85 + Math.random() * 0.35;
      s.set(1, scale, 1);
      q.setFromAxisAngle(UP, Math.random() * Math.PI * 2);
      m.compose(new THREE.Vector3(st.x, st.y, st.z), q, s);
      this.mesh.setMatrixAt(i, m);
    });
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  /** Repaint every stalk from a biome base colour (fresh per-stalk jitter). */
  retint(base: string): void {
    const col = new THREE.Color();
    for (let i = 0; i < this.stalks.length; i++) {
      col.set(base).offsetHSL((Math.random() - 0.5) * 0.02, 0, (Math.random() - 0.5) * 0.08);
      this.mesh.setColorAt(i, col);
    }
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  /** Stalks currently lying down. Matches `flattened`; kept as the check that
   *  the counter has not drifted from the geometry. */
  get downCount(): number {
    let n = 0;
    for (const st of this.stalks) if (st.down) n++;
    return n;
  }

  /** Immutable overhead record for the post-flight card. Captured only after
   * a run, never while flying, so the player's exact drawing can leave the
   * game without retaining or exposing the live stalk objects. */
  shareSnapshot(): CornSnapshot {
    const points = new Float32Array(this.stalks.length * 3);
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    let down = 0;
    for (let i = 0; i < this.stalks.length; i++) {
      const stalk = this.stalks[i];
      points[i * 3] = stalk.x;
      points[i * 3 + 1] = stalk.z;
      points[i * 3 + 2] = stalk.down ? (stalk.ink ? 2 : 1) : 0;
      minX = Math.min(minX, stalk.x);
      maxX = Math.max(maxX, stalk.x);
      minZ = Math.min(minZ, stalk.z);
      maxZ = Math.max(maxZ, stalk.z);
      if (stalk.down) down++;
    }
    if (this.stalks.length === 0) minX = maxX = minZ = maxZ = 0;
    return { points, minX, maxX, minZ, maxZ, down, knocked: this.knocked };
  }
}

const UP = new THREE.Vector3(0, 1, 0);
const FORWARD = new THREE.Vector3(0, 0, 1);

function naturalHeight(map: MapSpec, x: number, z: number): number {
  let h = map.field(x, z);
  // Lake basin, carved from whichever water body the map has.
  const ld = Math.hypot(x - map.lake.x, z - map.lake.z);
  const sigma = map.lake.r * 0.81;
  h -= 3.2 * Math.exp(-(ld * ld) / (2 * sigma * sigma));
  return h;
}

/**
 * Ground someone has levelled, as a segment: from (x1,z1) to (x2,z2), flat for
 * `half` either side, then a `ramp` back to the natural land. A pad whose ends
 * are the same point is a circular one.
 */
interface Pad {
  x1: number; z1: number; x2: number; z2: number;
  half: number; ramp: number;
  /** Natural height at each end, resolved once. Along the pad the level runs
   *  between them, so a terrace row or a street can climb without stepping. */
  levelA: number; levelB: number;
}

/**
 * The graded ground of a land, and the profile of its ring road.
 *
 * Two problems, one answer. First: a stone circle, a chapel, a terrace row are
 * laid out assuming the ground under them is level, and some of them are
 * literally flat discs of geometry — a glowing ring on a hillside cannot be
 * made to look right by any amount of per-part height offsetting. Second: a
 * road that simply drapes over a landform stops reading as a road, because
 * real ones are cut into hillsides and carried over dips on embankments.
 *
 * So the ground gives way: pads under the set pieces, and a smoothed profile
 * along the road corridor. Both are derived from what the map already
 * declares, so a land cannot gain a building and forget to grade under it.
 */
interface Grading {
  pads: Pad[];
  /** Road height by angle around the ring, smoothed. Sampled, not solved. */
  roadProfile: Float32Array;
}

const gradingCache = new WeakMap<MapSpec, Grading>();
const ROAD_SAMPLES = 256;
/** How far either side of the carriageway the cutting/embankment reaches. */
const ROAD_SHOULDER = 11;

function pad(map: MapSpec, x1: number, z1: number, x2: number, z2: number, half: number, ramp: number): Pad {
  return {
    x1, z1, x2, z2, half, ramp,
    levelA: naturalHeight(map, x1, z1),
    levelB: naturalHeight(map, x2, z2),
  };
}

function gradingFor(map: MapSpec): Grading {
  const hit = gradingCache.get(map);
  if (hit) return hit;

  const pads: Pad[] = [
    // Generous ramps: a smoothstep's steepest point is 1.5x its average, so a
    // pad cut into a butte skirt with a short ramp makes a cliff of its own.
    pad(map, ...([map.compoundAt[0], map.compoundAt[1], map.compoundAt[0], map.compoundAt[1], 10, 13] as const)),
    pad(map, ...([map.chapelAt[0], map.chapelAt[1], map.chapelAt[0], map.chapelAt[1], 7, 10] as const)),
    ...map.farms.map(([x, z]) => pad(map, x, z, x, z, 8, 11)),
    // The paddock is flat ground or the fence posts hang in the air and the
    // donkeys graze up a slope. Wider than a farm pad because the pen is wider.
    ...(map.donkeyFarm ? [pad(map, map.donkeyFarm[0], map.donkeyFarm[1],
      map.donkeyFarm[0], map.donkeyFarm[1], 12, 14)] : []),
    ...map.graveyards.map(([x, z]) => pad(map, x, z, x, z, 6, 9)),
    // A row of houses shares one foundation, and a street is a street.
    ...(map.terraces ?? []).map((row) => {
      const len = row.n * 3.4;
      return pad(map, row.cx, row.cz, row.cx + Math.cos(row.rot) * len, row.cz + Math.sin(row.rot) * len, 4.4, 7);
    }),
    ...(map.streets ?? []).map((s) => pad(map, s.x1, s.z1, s.x2, s.z2, s.halfW + 1.5, 6)),
  ];

  // The ring road's own profile: the natural ground under the carriageway,
  // then smoothed around the ring so the road rises and falls in long grades
  // instead of tracking every ripple it crosses.
  const raw = new Float32Array(ROAD_SAMPLES);
  for (let i = 0; i < ROAD_SAMPLES; i++) {
    const a = (i / ROAD_SAMPLES) * Math.PI * 2;
    raw[i] = naturalHeight(map, Math.cos(a) * map.road.r, Math.sin(a) * map.road.r);
  }
  // Smoothed twice: one moving average still leaves a corner where the ring
  // crosses something big, and a corner in a carriageway reads as a fault.
  const window = 11;
  let roadProfile = raw;
  for (let pass = 0; pass < 2; pass++) {
    const next = new Float32Array(ROAD_SAMPLES);
    for (let i = 0; i < ROAD_SAMPLES; i++) {
      let sum = 0;
      for (let k = -window; k <= window; k++) {
        sum += roadProfile[(i + k + ROAD_SAMPLES) % ROAD_SAMPLES];
      }
      next[i] = sum / (window * 2 + 1);
    }
    roadProfile = next;
  }

  const grading: Grading = { pads, roadProfile };
  gradingCache.set(map, grading);
  return grading;
}

/** Height of the graded carriageway at this bearing, interpolated. */
function roadLevel(profile: Float32Array, x: number, z: number): number {
  const a = Math.atan2(z, x) / (Math.PI * 2) * ROAD_SAMPLES;
  const i = Math.floor(a);
  const f = a - i;
  const p = profile[((i % ROAD_SAMPLES) + ROAD_SAMPLES) % ROAD_SAMPLES];
  const q = profile[((i + 1) % ROAD_SAMPLES + ROAD_SAMPLES) % ROAD_SAMPLES];
  return p + (q - p) * f;
}

const smoothstep = (t: number): number => t * t * (3 - 2 * t);

function fieldHeight(map: MapSpec, x: number, z: number): number {
  let h = naturalHeight(map, x, z);
  const { pads, roadProfile } = gradingFor(map);

  for (const p of pads) {
    const dx = p.x2 - p.x1;
    const dz = p.z2 - p.z1;
    const len2 = dx * dx + dz * dz;
    const t = len2 > 0
      ? THREE.MathUtils.clamp(((x - p.x1) * dx + (z - p.z1) * dz) / len2, 0, 1)
      : 0;
    const d = Math.hypot(x - (p.x1 + dx * t), z - (p.z1 + dz * t));
    if (d > p.half + p.ramp) continue;
    const level = p.levelA + (p.levelB - p.levelA) * t;
    h += (level - h) * smoothstep(THREE.MathUtils.clamp((p.half + p.ramp - d) / p.ramp, 0, 1));
  }

  // The road corridor: flat across the carriageway, then a cutting or an
  // embankment out to the shoulder.
  const fromCentre = Math.hypot(x, z);
  const offRoad = Math.abs(fromCentre - map.road.r);
  if (offRoad < map.road.halfW + ROAD_SHOULDER) {
    const blend = smoothstep(THREE.MathUtils.clamp(
      (map.road.halfW + ROAD_SHOULDER - offRoad) / ROAD_SHOULDER, 0, 1));
    h += (roadLevel(roadProfile, x, z) - h) * blend;
  }

  // Mountain ring walls the play area (every land sits in the same crater).
  if (fromCentre > PLAY_RADIUS) {
    const t = (fromCentre - PLAY_RADIUS) / 30;
    h += t * t * 26;
  }
  return h;
}

/**
 * The range of ground under a footprint: what a thing has to span to stand on
 * it. Seat a building at the low end and it sinks into the hill; seat it at the
 * high end and it hangs over the slope. Callers want both numbers so they can
 * pick the high one and fill the difference with a foundation.
 */
export function groundRange(x: number, z: number, radius: number): { low: number; high: number } {
  let low = heightAt(x, z);
  let high = low;
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const h = heightAt(x + Math.cos(a) * radius, z + Math.sin(a) * radius);
    low = Math.min(low, h);
    high = Math.max(high, h);
  }
  return { low, high };
}

/** The lowest ground under a footprint. For things whose legs can be buried
 *  without anyone noticing — a lattice tower, a drystone wall. */
export function groundUnder(x: number, z: number, radius: number): number {
  return groundRange(x, z, radius).low;
}

export function heightAt(x: number, z: number): number {
  return fieldHeight(activeMap, x, z);
}

/** The ground of a land that is not the one currently loaded. Map layout is
 *  pure data, so this needs no scene — which is what lets the tests check that
 *  the wires of every land hang where they are supposed to. */
export function heightIn(id: BiomeId, x: number, z: number): number {
  return fieldHeight(MAPS[id], x, z);
}

function makeGlowTexture(inner: string, outer: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 128;
  const g = c.getContext('2d')!;
  const grad = g.createRadialGradient(64, 64, 4, 64, 64, 64);
  grad.addColorStop(0, inner);
  grad.addColorStop(1, outer);
  g.fillStyle = grad;
  g.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

/** A tapered, transparent flame silhouette; unlike a radial glow it keeps a
 *  readable lick and base when the ritual camera moves in close. */
function makeFlameTexture(core: string, middle: string, edge: string): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 192;
  const g = c.getContext('2d')!;
  const grad = g.createLinearGradient(64, 8, 64, 178);
  grad.addColorStop(0, edge);
  grad.addColorStop(0.42, middle);
  grad.addColorStop(0.78, core);
  grad.addColorStop(1, edge);
  g.fillStyle = grad;
  g.shadowColor = middle;
  g.shadowBlur = 14;
  g.beginPath();
  g.moveTo(64, 7);
  g.bezierCurveTo(82, 43, 113, 76, 103, 128);
  g.bezierCurveTo(96, 168, 78, 181, 63, 181);
  g.bezierCurveTo(37, 181, 19, 159, 25, 126);
  g.bezierCurveTo(31, 89, 52, 61, 64, 7);
  g.fill();
  return new THREE.CanvasTexture(c);
}

function buildSky(scene: THREE.Scene): { moonMat: THREE.MeshBasicMaterial; moonHaloMat: THREE.SpriteMaterial } {
  const geo = new THREE.SphereGeometry(420, 24, 12);
  const pos = geo.attributes.position;
  const colors = new Float32Array(pos.count * 3);
  const top = new THREE.Color('#1a1f3d');
  const horizon = new THREE.Color('#0b0d1a');
  const tmp = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const y = pos.getY(i) / 420;
    tmp.copy(horizon).lerp(top, Math.max(0, y));
    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  const mat = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide, fog: false, depthWrite: false });
  const sky = new THREE.Mesh(geo, mat);
  sky.renderOrder = -10;
  scene.add(sky);

  // Stars on the upper dome.
  const starCount = 700;
  const starPos = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(Math.random() * 0.85 + 0.12);
    const r = 400;
    starPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    starPos[i * 3 + 1] = r * Math.cos(phi);
    starPos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
  }
  const starGeo = new THREE.BufferGeometry();
  starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
  const starMat = new THREE.PointsMaterial({ color: '#cdd6ff', size: 1.6, sizeAttenuation: false, fog: false, transparent: true, opacity: 0.85 });
  const stars = new THREE.Points(starGeo, starMat);
  stars.renderOrder = -9;
  scene.add(stars);

  // Big brand-orange moon low over the mountains, with additive halo.
  const moonPos = new THREE.Vector3(-190, 95, -330);
  const moonMat = new THREE.MeshBasicMaterial({ color: '#ffb347', fog: false });
  const moon = new THREE.Mesh(new THREE.CircleGeometry(34, 40), moonMat);
  moon.position.copy(moonPos);
  moon.lookAt(0, 40, 0);
  moon.renderOrder = -8;
  scene.add(moon);
  const moonHaloMat = new THREE.SpriteMaterial({
    map: makeGlowTexture('rgba(247,147,26,0.55)', 'rgba(247,147,26,0)'),
    blending: THREE.AdditiveBlending,
    fog: false,
    depthWrite: false,
  });
  const halo = new THREE.Sprite(moonHaloMat);
  halo.scale.setScalar(160);
  halo.position.copy(moonPos);
  halo.renderOrder = -7;
  scene.add(halo);
  return { moonMat, moonHaloMat };
}

/** Repaint terrain vertex colours from a biome palette. */
function paintTerrain(geo: THREE.BufferGeometry, p: BiomePalette): void {
  const pos = geo.attributes.position;
  const col = geo.attributes.color as THREE.BufferAttribute;
  const colors = col.array as Float32Array;
  const low = new THREE.Color(p.low);
  const high = new THREE.Color(p.high);
  const rock = new THREE.Color(p.rock);
  const rockHi = new THREE.Color(p.rockHi);
  const deep = new THREE.Color(p.deep);
  const tmp = new THREE.Color();
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const z = pos.getZ(i);
    const h = pos.getY(i);
    if (h > p.rockLine) tmp.copy(rock).lerp(rockHi, Math.min((h - p.rockLine) / 22, 1));
    else if (h < -0.75) tmp.copy(deep);
    else tmp.copy(low).lerp(high, THREE.MathUtils.clamp((h + 2) / 6, 0, 1));
    const jitter = (Math.sin(x * 12.9898 + z * 78.233) * 43758.5453) % 1;
    tmp.offsetHSL(0, 0, (jitter - 0.5) * 0.014);
    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }
  col.needsUpdate = true;
}

/** Re-sample the shared terrain sheet from the ACTIVE map's heightfield. */
function morphTerrain(geo: THREE.BufferGeometry): void {
  const pos = geo.attributes.position;
  for (let i = 0; i < pos.count; i++) {
    pos.setY(i, heightAt(pos.getX(i), pos.getZ(i)));
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
}

function buildTerrain(scene: THREE.Scene): THREE.BufferGeometry {
  const size = 460;
  const segs = 180;
  const geo = new THREE.PlaneGeometry(size, size, segs, segs);
  geo.rotateX(-Math.PI / 2);
  geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array(geo.attributes.position.count * 3), 3));
  morphTerrain(geo);
  paintTerrain(geo, BIOMES.valley);
  const mat = new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true });
  scene.add(new THREE.Mesh(geo, mat));
  return geo;
}

function scatterSpots(count: number, minR: number, maxR: number, avoid: THREE.Vector3[], seedBase: number): THREE.Vector3[] {
  const spots: THREE.Vector3[] = [];
  let seed = seedBase;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  let guard = 0;
  while (spots.length < count && guard++ < count * 30) {
    const a = rand() * Math.PI * 2;
    const r = minR + rand() * (maxR - minR);
    const p = new THREE.Vector3(Math.cos(a) * r, 0, Math.sin(a) * r);
    if (avoid.some((v) => v.distanceTo(p) < 13)) continue;
    if (onRoad(p.x, p.z, 1.8)) continue; // keep the carriageway clear
    p.y = heightAt(p.x, p.z);
    spots.push(p);
  }
  return spots;
}

/** Repaint the road ribbon for a biome (packed dirt, sand, or snow). */
function paintRoad(geo: THREE.BufferGeometry, p: BiomePalette): void {
  const col = geo.attributes.color as THREE.BufferAttribute;
  const colors = col.array as Float32Array;
  const dirt = new THREE.Color(p.road);
  const tmp = new THREE.Color();
  for (let i = 0; i < col.count; i++) {
    const jitter = (Math.sin(Math.floor(i / 2) * 12.9898 + (i % 2) * 78.233) * 43758.5453) % 1;
    tmp.copy(dirt).offsetHSL(0, 0, (jitter - 0.5) * 0.05);
    colors[i * 3] = tmp.r;
    colors[i * 3 + 1] = tmp.g;
    colors[i * 3 + 2] = tmp.b;
  }
  col.needsUpdate = true;
}

const ROAD_SEGS = 160;

/** Re-lay the shared ring-road strip from the ACTIVE map's ROAD + terrain. */
function layRoad(geo: THREE.BufferGeometry): void {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  const positions = pos.array as Float32Array;
  const inner = ROAD.r - ROAD.halfW;
  const outer = ROAD.r + ROAD.halfW;
  for (let i = 0; i <= ROAD_SEGS; i++) {
    const a = (i / ROAD_SEGS) * Math.PI * 2;
    const ca = Math.cos(a);
    const sa = Math.sin(a);
    for (let k = 0; k < 2; k++) {
      const r = k === 0 ? inner : outer;
      const x = ca * r;
      const z = sa * r;
      const idx = (i * 2 + k) * 3;
      positions[idx] = x;
      positions[idx + 1] = heightAt(x, z) + 0.08;
      positions[idx + 2] = z;
    }
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals();
}

/** Dirt ring road hugging the terrain — the convoy route. */
function buildRoad(scene: THREE.Scene): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array((ROAD_SEGS + 1) * 2 * 3), 3));
  geo.setAttribute('color', new THREE.BufferAttribute(new Float32Array((ROAD_SEGS + 1) * 2 * 3), 3));
  const indices: number[] = [];
  for (let i = 0; i < ROAD_SEGS; i++) {
    const a = i * 2;
    const b = i * 2 + 1;
    const c = (i + 1) * 2;
    const d = (i + 1) * 2 + 1;
    indices.push(a, c, b, b, c, d);
  }
  geo.setIndex(indices);
  layRoad(geo);
  paintRoad(geo, BIOMES.valley);
  const road = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true }));
  road.renderOrder = 0;
  scene.add(road);
  return geo;
}

/** The First Temple of the 600 — a roofless chapel ruin, candles still lit. */
function buildChapel(parent: THREE.Object3D, at: THREE.Vector3): void {
  const group = new THREE.Group();
  const stone = new THREE.MeshLambertMaterial({ color: '#565e72', flatShading: true });
  const oldStone = new THREE.MeshLambertMaterial({ color: '#474e60', flatShading: true });

  // Two broken side walls, crumbling to different heights.
  for (const [dz, segs] of [[-2.6, 4], [2.6, 5]] as const) {
    for (let i = 0; i < segs; i++) {
      const h = 1.2 + Math.abs(Math.sin(i * 2.7 + dz)) * 1.6;
      const wall = new THREE.Mesh(new THREE.BoxGeometry(1.5, h, 0.5), i % 2 ? stone : oldStone);
      wall.position.set(-3 + i * 1.55, h / 2, dz);
      wall.rotation.z = Math.sin(i * 4.1 + dz) * 0.05;
      group.add(wall);
    }
  }
  // Gable end with an arch: two pillars and a lintel.
  for (const dz of [-1.1, 1.1]) {
    const pillar = new THREE.Mesh(new THREE.BoxGeometry(0.7, 3.4, 0.6), stone);
    pillar.position.set(-3.9, 1.7, dz);
    group.add(pillar);
  }
  const lintel = new THREE.Mesh(new THREE.BoxGeometry(0.7, 0.55, 2.9), oldStone);
  lintel.position.set(-3.9, 3.6, 0);
  group.add(lintel);
  // Fallen roof beams.
  const beamMat = new THREE.MeshLambertMaterial({ color: '#31281e', flatShading: true });
  for (let i = 0; i < 3; i++) {
    const beam = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.18, 3.6), beamMat);
    beam.position.set(-1.5 + i * 1.7, 0.5 + i * 0.12, (i % 2 ? 1 : -1) * 0.5);
    beam.rotation.set(0.4 + i * 0.5, i * 1.1, 0.2);
    group.add(beam);
  }
  // The altar tablet: the ledger carved in stone, glowing faintly.
  const altar = new THREE.Mesh(
    new THREE.BoxGeometry(1.1, 2.2, 0.5),
    [stone, stone, stone, stone, new THREE.MeshBasicMaterial({ map: makeStoneTexture() }), stone],
  );
  altar.position.set(2.6, 1.05, 0);
  altar.rotation.y = Math.PI;
  altar.rotation.z = 0.04;
  group.add(altar);
  // Votive candles: warm glow sprites, no extra lights on the budget.
  const glow = makeGlowTexture('rgba(255,214,120,0.8)', 'rgba(247,140,26,0)');
  for (const [cx, cz] of [[2.1, -0.9], [2.3, 0.8], [-3.5, 0.1], [0.2, -2.2], [-0.8, 2.1]]) {
    const candle = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.1, 0.28, 6), new THREE.MeshLambertMaterial({ color: '#e8dcc0' }));
    const y = heightAt(at.x + cx, at.z + cz) - heightAt(at.x, at.z);
    candle.position.set(cx, y + 0.14, cz);
    group.add(candle);
    const flame = new THREE.Sprite(new THREE.SpriteMaterial({
      map: glow,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      opacity: 0.85,
    }));
    flame.scale.set(0.55, 0.8, 1);
    flame.position.set(cx, y + 0.5, cz);
    group.add(flame);
  }
  group.position.set(at.x, heightAt(at.x, at.z), at.z);
  group.rotation.y = -0.5;
  parent.add(group);
}

/** Repaint the instanced canopies for a biome. */
function paintCanopies(canopies: THREE.InstancedMesh, count: number, base: string): void {
  const col = new THREE.Color();
  for (let i = 0; i < count; i++) {
    col.set(base).offsetHSL(0, 0, (((i * 53) % 9) - 4) * 0.012);
    canopies.setColorAt(i, col);
  }
  if (canopies.instanceColor) canopies.instanceColor.needsUpdate = true;
}

function buildProps(parent: THREE.Object3D, avoid: THREE.Vector3[], trees: MapSpec['trees'], rocks: MapSpec['rocks']): {
  canopies: THREE.InstancedMesh;
  treeCount: number;
  treeSpots: THREE.Vector3[];
  rockSpots: THREE.Vector3[];
  clearSpots: THREE.Vector3[];
  rockMat: THREE.MeshLambertMaterial;
} {
  // Pines: instanced trunk + canopy.
  const treeSpots = scatterSpots(trees.count, 16, 132, avoid, trees.seed);
  const trunkGeo = new THREE.CylinderGeometry(0.14, 0.22, 1.2, 5);
  const canopyGeo = new THREE.ConeGeometry(1.15, 3.1, 6);
  const trunkMat = new THREE.MeshLambertMaterial({ color: '#4c3a26', flatShading: true });
  const canopyMat = new THREE.MeshLambertMaterial({ color: '#ffffff', flatShading: true });
  const trunks = new THREE.InstancedMesh(trunkGeo, trunkMat, treeSpots.length);
  trunks.name = 'tree-trunks';
  const canopies = new THREE.InstancedMesh(canopyGeo, canopyMat, treeSpots.length);
  canopies.name = 'tree-canopies';
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3();
  treeSpots.forEach((p, i) => {
    const scale = 0.8 + ((i * 37) % 10) / 14;
    s.setScalar(scale);
    q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), (i * 2.39996) % (Math.PI * 2));
    m.compose(new THREE.Vector3(p.x, p.y + 0.55 * scale, p.z), q, s);
    trunks.setMatrixAt(i, m);
    m.compose(new THREE.Vector3(p.x, p.y + (1.2 + 1.4) * scale, p.z), q, s);
    canopies.setMatrixAt(i, m);
  });
  paintCanopies(canopies, treeSpots.length, BIOMES.valley.canopy);
  parent.add(trunks, canopies);

  // Rocks.
  const rockSpots = scatterSpots(rocks.count, 12, 122, avoid, rocks.seed);
  const rockGeo = new THREE.DodecahedronGeometry(0.7, 0);
  const rockMat = new THREE.MeshLambertMaterial({ color: '#4c5570', flatShading: true });
  const boulders = new THREE.InstancedMesh(rockGeo, rockMat, rockSpots.length);
  boulders.name = 'boulders';
  rockSpots.forEach((p, i) => {
    const scale = 0.5 + ((i * 29) % 12) / 12;
    s.set(scale, scale * (0.6 + ((i * 13) % 5) / 10), scale);
    q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), i * 1.7);
    m.compose(new THREE.Vector3(p.x, p.y + 0.25 * scale, p.z), q, s);
    boulders.setMatrixAt(i, m);
  });
  parent.add(boulders);
  return { canopies, treeCount: treeSpots.length, treeSpots, rockSpots, clearSpots: avoid, rockMat };
}

function buildFarm(parent: THREE.Object3D, at: THREE.Vector3, biome: BiomeId): void {
  const group = new THREE.Group();
  if (biome === 'town') {
    // A depot: flat-roofed unit, roller shutter, sodium lamp over the yard.
    const unit = new THREE.Mesh(
      new THREE.BoxGeometry(6.4, 3.1, 4.2),
      new THREE.MeshLambertMaterial({ color: '#5c6068', flatShading: true }),
    );
    unit.position.y = 1.55;
    const parapet = new THREE.Mesh(
      new THREE.BoxGeometry(6.7, 0.26, 4.5),
      new THREE.MeshLambertMaterial({ color: '#494d54', flatShading: true }),
    );
    parapet.position.y = 3.22;
    const shutter = new THREE.Mesh(
      new THREE.PlaneGeometry(2.6, 2.2),
      new THREE.MeshBasicMaterial({ color: '#2a2f36' }),
    );
    shutter.position.set(0, 1.1, 2.11);
    group.add(unit, parapet, shutter);
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.09, 0.11, 4.4, 6),
      new THREE.MeshLambertMaterial({ color: '#3a3e44', flatShading: true }),
    );
    post.position.set(3.9, 2.2, 2.6);
    group.add(post);
    const lamp = new THREE.Sprite(new THREE.SpriteMaterial({
      map: makeGlowTexture('rgba(255,196,110,0.75)', 'rgba(247,140,26,0)'),
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      opacity: 0.8,
    }));
    lamp.scale.setScalar(5.2);
    lamp.position.set(3.9, 4.4, 2.6);
    group.add(lamp);
  } else if (biome === 'moor') {
    // A stone byre with a corrugated roof, half sunk into the slope.
    const byre = new THREE.Mesh(
      new THREE.BoxGeometry(4.6, 2.2, 3.0),
      new THREE.MeshLambertMaterial({ color: '#767a72', flatShading: true }),
    );
    byre.position.y = 1.1;
    const roof = new THREE.Mesh(
      new THREE.BoxGeometry(5.0, 0.16, 3.4),
      new THREE.MeshLambertMaterial({ color: '#5c5f5a', flatShading: true }),
    );
    roof.position.y = 2.3;
    roof.rotation.z = 0.12;
    const gate = new THREE.Mesh(
      new THREE.PlaneGeometry(1.6, 1.3),
      new THREE.MeshBasicMaterial({ color: '#2a2620' }),
    );
    gate.position.set(0, 0.65, 1.51);
    group.add(byre, roof, gate);
    const pen = new THREE.MeshLambertMaterial({ color: '#5c5244', flatShading: true });
    for (const [px, pz] of [[-2.8, 2.2], [0, 3.0], [2.8, 2.2]]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(2.8, 0.1, 0.1), pen);
      rail.position.set(px, 0.8, pz);
      rail.rotation.y = px === 0 ? 0 : 0.5 * Math.sign(px);
      group.add(rail);
    }
  } else {
    const barn = new THREE.Mesh(
      new THREE.BoxGeometry(4.4, 2.6, 3.2),
      new THREE.MeshLambertMaterial({ color: '#8c3a2c', flatShading: true }),
    );
    barn.position.y = 1.3;
    const roof = new THREE.Mesh(
      new THREE.CylinderGeometry(0.01, 2.6, 1.7, 4, 1),
      new THREE.MeshLambertMaterial({ color: '#5c5244', flatShading: true }),
    );
    roof.rotation.y = Math.PI / 4;
    roof.scale.z = 0.78;
    roof.position.y = 3.45;
    const door = new THREE.Mesh(
      new THREE.PlaneGeometry(1.2, 1.6),
      new THREE.MeshBasicMaterial({ color: '#2a1a12' }),
    );
    door.position.set(0, 0.8, 1.61);
    group.add(barn, roof, door);
  }
  group.position.set(at.x, heightAt(at.x, at.z), at.z);
  group.rotation.y = 0.5;
  parent.add(group);
}

/** The farm sign, painted on board. Read from the air at beam height, so the
 *  lettering is oversized and the board is high-contrast rather than pretty. */
function makeDonkeySignTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 128;
  const g = c.getContext('2d')!;
  g.fillStyle = '#f2ede4';
  g.fillRect(0, 0, 256, 128);
  g.strokeStyle = '#5c3a22';
  g.lineWidth = 7;
  g.strokeRect(4, 4, 248, 120);
  g.fillStyle = '#5c3a22';
  g.textAlign = 'center';
  g.textBaseline = 'middle';
  g.font = 'bold 30px system-ui, sans-serif';
  g.fillText('THE', 128, 30);
  g.font = 'bold 44px system-ui, sans-serif';
  g.fillText('DONKEY', 128, 68);
  g.font = 'bold 34px system-ui, sans-serif';
  g.fillText('FARM', 128, 104);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

let donkeySignTex: THREE.CanvasTexture | null = null;

/**
 * The Donkey Farm: a stable, a hay store and a rail paddock with its name on
 * the gate. The pen is built as a ring of posts and rails rather than a solid
 * wall so the herd inside stays readable from above — the pilot has to be able
 * to count donkeys through it while lining up a beam.
 */
function buildDonkeyFarm(parent: THREE.Object3D, at: THREE.Vector3): void {
  const group = new THREE.Group();
  const timber = new THREE.MeshLambertMaterial({ color: '#6b4a30', flatShading: true });
  const wall = new THREE.MeshLambertMaterial({ color: '#a9784e', flatShading: true });
  const roofMat = new THREE.MeshLambertMaterial({ color: '#4c4238', flatShading: true });

  // Stable: a long open-fronted block, so the beam can still reach inside.
  const stable = new THREE.Mesh(new THREE.BoxGeometry(6.2, 2.5, 3.4), wall);
  stable.position.set(0, 1.25, -6.6);
  const roof = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 2.9, 1.5, 4, 1), roofMat);
  roof.rotation.y = Math.PI / 4;
  roof.position.set(0, 3.05, -6.6);
  roof.scale.set(1.5, 1, 0.86);
  group.add(stable, roof);

  // Hay: the reason the herd stays put, and a bit of colour in the pen.
  const hayMat = new THREE.MeshLambertMaterial({ color: '#c8a552', flatShading: true });
  for (const [hx, hz] of [[-4.4, -3.2], [-3.4, -4.4]]) {
    const bale = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 1.1, 8), hayMat);
    bale.rotation.z = Math.PI / 2;
    bale.position.set(hx, 0.62, hz);
    group.add(bale);
  }

  // The paddock: posts on a ring, rails strung between neighbours, and a gap
  // left at the front for the gate the sign hangs on.
  // The gate faces +Z, the side the fixed camera looks in from, so the sign
  // over it is square to the pilot instead of edge-on.
  const RADIUS = 8.4;
  const POSTS = 22;
  // A quarter turn round the ring is +Z, the side the camera looks in from, so
  // the two spans either side of it are the gateway the sign stands over.
  const GATE = Math.round(POSTS / 4);
  for (let i = 0; i < POSTS; i++) {
    const a = (i / POSTS) * Math.PI * 2;
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.18, 0.16), timber);
    post.position.set(Math.cos(a) * RADIUS, 0.59, Math.sin(a) * RADIUS);
    group.add(post);
    if (i === GATE || i === GATE + 1) continue; // no rails across the entrance
    const b = ((i - 1) / POSTS) * Math.PI * 2;
    const from = new THREE.Vector3(Math.cos(b) * RADIUS, 0, Math.sin(b) * RADIUS);
    const to = new THREE.Vector3(Math.cos(a) * RADIUS, 0, Math.sin(a) * RADIUS);
    const span = to.clone().sub(from);
    for (const height of [0.5, 0.95]) {
      const rail = new THREE.Mesh(new THREE.BoxGeometry(span.length(), 0.1, 0.08), timber);
      rail.position.copy(from).add(to).multiplyScalar(0.5).setY(height);
      rail.rotation.y = -Math.atan2(span.z, span.x);
      group.add(rail);
    }
  }

  // The sign over the gate, laid back so it reads from a saucer looking down
  // rather than from the ground nobody is standing on.
  donkeySignTex ??= makeDonkeySignTexture();
  const board = new THREE.Mesh(
    new THREE.PlaneGeometry(4.6, 2.3),
    new THREE.MeshBasicMaterial({ map: donkeySignTex, side: THREE.DoubleSide, toneMapped: false }),
  );
  board.position.set(0, 3.0, RADIUS + 0.5);
  board.rotation.x = -0.5;
  const postGeo = new THREE.BoxGeometry(0.2, 3.1, 0.2);
  for (const side of [-1, 1]) {
    const mast = new THREE.Mesh(postGeo, timber);
    mast.position.set(side * 2.1, 1.55, RADIUS + 0.5);
    group.add(mast);
  }
  group.add(board);

  group.position.set(at.x, heightAt(at.x, at.z), at.z);
  parent.add(group);
}

/** Terrace front: brick, a door, and two windows — one of them still up. */
function makeTerraceFaceTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const g = c.getContext('2d')!;
  g.fillStyle = '#7a4636';
  g.fillRect(0, 0, 64, 64);
  // Brick courses.
  g.strokeStyle = 'rgba(30,18,14,0.35)';
  g.lineWidth = 1;
  for (let y = 4; y < 64; y += 5) {
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(64, y);
    g.stroke();
  }
  // Upstairs windows: one lit, one dark.
  g.fillStyle = '#ffd98a';
  g.fillRect(9, 12, 15, 17);
  g.fillStyle = '#1b2230';
  g.fillRect(40, 12, 15, 17);
  // Downstairs: bay window and a front door.
  g.fillStyle = '#ffcf7a';
  g.fillRect(7, 40, 19, 18);
  g.fillStyle = '#2a1f18';
  g.fillRect(38, 38, 14, 26);
  g.fillStyle = '#e8c86a';
  g.fillRect(48, 48, 2, 2);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

let terraceFaceTex: THREE.CanvasTexture | null = null;

/**
 * A street of terraced housing. Each row is one long brick body with one
 * pitched-ridge prism over it (a box turned 45 degrees reads as a roof from
 * this camera), and the individual houses come from an instanced front face
 * plus instanced chimney stacks — so a whole town is a handful of draw calls
 * rather than one per house.
 */
function buildTerraces(parent: THREE.Object3D, rows: NonNullable<MapSpec['terraces']>): void {
  const HOUSE = 3.4;
  const DEPTH = 4.4;
  const WALL_H = 3.0;
  const brick = new THREE.MeshLambertMaterial({ color: '#7a4636', flatShading: true });
  // Wet slate, not black: the ridge is lit almost edge-on from the moon side,
  // so a dark grey roof reads as a hole in the row from this camera.
  const slate = new THREE.MeshLambertMaterial({ color: '#5a6270', flatShading: true });
  const stack = new THREE.MeshLambertMaterial({ color: '#6d3f34', flatShading: true });
  const potMat = new THREE.MeshLambertMaterial({ color: '#c56a4a', flatShading: true });
  terraceFaceTex ??= makeTerraceFaceTexture();

  const faces: { x: number; y: number; z: number; rot: number }[] = [];
  const stacks: { x: number; y: number; z: number }[] = [];

  for (const row of rows) {
    const length = row.n * HOUSE;
    const cos = Math.cos(row.rot);
    const sin = Math.sin(row.rot);
    // Sit the row on the LOWEST ground across its whole depth, not just under
    // the centreline. The front edge faces the camera, so even a small gap
    // there makes a terrace look as if it is floating above the street.
    let baseY = Infinity;
    for (let i = 0; i <= row.n; i++) {
      const t = i * HOUSE;
      baseY = Math.min(baseY, groundUnder(row.cx + cos * t, row.cz + sin * t, DEPTH / 2));
    }
    const midX = row.cx + cos * (length / 2 - HOUSE / 2);
    const midZ = row.cz + sin * (length / 2 - HOUSE / 2);

    const body = new THREE.Mesh(new THREE.BoxGeometry(length, WALL_H, DEPTH), brick);
    body.position.set(midX, baseY + WALL_H / 2 - 0.2, midZ);
    body.rotation.y = -row.rot;
    body.name = 'terrace-row';
    body.userData.footprint = [row.cx, row.cz, cos, sin, length];
    parent.add(body);
    // The ridge: a long box rolled 45 degrees about its length, so its lower
    // vertex is buried in the walls and its apex is the roof line. Kept a shade
    // narrower than the walls so the eaves are inset and the brick front is
    // still visible from above.
    const roof = new THREE.Mesh(new THREE.BoxGeometry(length, 2.6, 2.6), slate);
    roof.position.set(midX, baseY + WALL_H - 0.15, midZ);
    roof.rotation.y = -row.rot;
    roof.rotateX(Math.PI / 4);
    parent.add(roof);

    // The row's street-facing normal: (sin, 0, -cos) of its heading.
    const nx = sin;
    const nz = -cos;
    for (let i = 0; i < row.n; i++) {
      const t = i * HOUSE;
      const hx = row.cx + cos * t;
      const hz = row.cz + sin * t;
      faces.push({
        x: hx + nx * (DEPTH / 2 + 0.02),
        y: baseY + 1.3,
        z: hz + nz * (DEPTH / 2 + 0.02),
        // A plane's +Z must end up along that normal, which is a yaw of pi - rot.
        rot: Math.PI - row.rot,
      });
      stacks.push({ x: hx, y: baseY + WALL_H + 1.4, z: hz });
    }
  }

  const faceMesh = new THREE.InstancedMesh(
    new THREE.PlaneGeometry(HOUSE * 0.92, 2.9),
    new THREE.MeshBasicMaterial({ map: terraceFaceTex, side: THREE.DoubleSide }),
    faces.length,
  );
  faceMesh.name = 'terrace-faces';
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const one = new THREE.Vector3(1, 1, 1);
  faces.forEach((f, i) => {
    q.setFromAxisAngle(UP, f.rot);
    m.compose(new THREE.Vector3(f.x, f.y, f.z), q, one);
    faceMesh.setMatrixAt(i, m);
  });
  parent.add(faceMesh);

  const stackMesh = new THREE.InstancedMesh(new THREE.BoxGeometry(0.5, 1.4, 0.7), stack, stacks.length);
  stackMesh.name = 'chimney-stacks';
  const potMesh = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.09, 0.1, 0.34, 6), potMat, stacks.length * 2);
  potMesh.name = 'chimney-pots';
  stacks.forEach((s, i) => {
    q.identity();
    m.compose(new THREE.Vector3(s.x, s.y, s.z), q, one);
    stackMesh.setMatrixAt(i, m);
    for (let k = 0; k < 2; k++) {
      m.compose(new THREE.Vector3(s.x, s.y + 0.85, s.z + (k ? 0.18 : -0.18)), q, one);
      potMesh.setMatrixAt(i * 2 + k, m);
    }
  });
  parent.add(stackMesh, potMesh);
}

/**
 * Metalled streets, all of them in one geometry: each is a strip sampled along
 * its centreline so it hugs the terrain, exactly like the ring road.
 */
function buildStreets(parent: THREE.Object3D, streets: NonNullable<MapSpec['streets']>): void {
  const positions: number[] = [];
  const indices: number[] = [];
  const marks: { x: number; z: number; y: number; rot: number }[] = [];
  for (const s of streets) {
    const dx = s.x2 - s.x1;
    const dz = s.z2 - s.z1;
    const length = Math.hypot(dx, dz);
    const segs = Math.max(2, Math.round(length / 3));
    const nx = -dz / length;
    const nz = dx / length;
    const base = positions.length / 3;
    for (let i = 0; i <= segs; i++) {
      const t = i / segs;
      const cx = s.x1 + dx * t;
      const cz = s.z1 + dz * t;
      for (const side of [-1, 1]) {
        const x = cx + nx * s.halfW * side;
        const z = cz + nz * s.halfW * side;
        positions.push(x, heightAt(x, z) + 0.07, z);
      }
      if (i % 2 === 0 && i < segs) {
        marks.push({ x: cx, z: cz, y: heightAt(cx, cz) + 0.09, rot: Math.atan2(dz, dx) });
      }
    }
    for (let i = 0; i < segs; i++) {
      const a = base + i * 2;
      indices.push(a, a + 2, a + 1, a + 1, a + 2, a + 3);
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
  geo.setIndex(indices);
  geo.computeVertexNormals();
  const road = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: '#2e2f33', flatShading: true }));
  road.renderOrder = 0;
  parent.add(road);

  // Centre lines, instanced. The geometry is pre-laid flat so each instance
  // only carries a yaw.
  const dashGeo = new THREE.PlaneGeometry(1.5, 0.16);
  dashGeo.rotateX(-Math.PI / 2);
  const dashes = new THREE.InstancedMesh(
    dashGeo,
    new THREE.MeshBasicMaterial({ color: '#b8b49a' }),
    marks.length,
  );
  dashes.name = 'street-dashes';
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const one = new THREE.Vector3(1, 1, 1);
  marks.forEach((mark, i) => {
    q.setFromAxisAngle(UP, -mark.rot);
    m.compose(new THREE.Vector3(mark.x, mark.y, mark.z), q, one);
    dashes.setMatrixAt(i, m);
  });
  parent.add(dashes);

  // Sodium street lamps down alternate kerbs.
  const lampGlow = makeGlowTexture('rgba(255,190,110,0.7)', 'rgba(247,140,26,0)');
  const posts: THREE.Vector3[] = [];
  for (const s of streets) {
    const dx = s.x2 - s.x1;
    const dz = s.z2 - s.z1;
    const length = Math.hypot(dx, dz);
    const n = Math.max(2, Math.floor(length / 22));
    const nx = -dz / length;
    const nz = dx / length;
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const side = i % 2 === 0 ? 1 : -1;
      const x = s.x1 + dx * t + nx * (s.halfW + 0.7) * side;
      const z = s.z1 + dz * t + nz * (s.halfW + 0.7) * side;
      posts.push(new THREE.Vector3(x, heightAt(x, z), z));
    }
  }
  const postMesh = new THREE.InstancedMesh(
    new THREE.CylinderGeometry(0.08, 0.1, 4.2, 5),
    new THREE.MeshLambertMaterial({ color: '#3a3e44', flatShading: true }),
    posts.length,
  );
  postMesh.name = 'lamp-posts';
  posts.forEach((p, i) => {
    q.identity();
    m.compose(new THREE.Vector3(p.x, p.y + 2.1, p.z), q, one);
    postMesh.setMatrixAt(i, m);
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({
      map: lampGlow,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      transparent: true,
      opacity: 0.7,
    }));
    halo.scale.setScalar(4.4);
    halo.position.set(p.x, p.y + 4.3, p.z);
    parent.add(halo);
  });
  parent.add(postMesh);
}

/** Drystone walls: instanced stone stepped along each line, over the tops. */
function buildWalls(parent: THREE.Object3D, walls: NonNullable<MapSpec['walls']>): void {
  const spots: { x: number; z: number; y: number; rot: number; i: number }[] = [];
  for (const [x1, z1, x2, z2] of walls) {
    const dx = x2 - x1;
    const dz = z2 - z1;
    const length = Math.hypot(dx, dz);
    const steps = Math.max(1, Math.round(length / 1.1));
    const rot = Math.atan2(dz, dx);
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = x1 + dx * t;
      const z = z1 + dz * t;
      spots.push({ x, z, y: heightAt(x, z), rot, i });
    }
  }
  const mesh = new THREE.InstancedMesh(
    new THREE.BoxGeometry(1.15, 0.78, 0.42),
    new THREE.MeshLambertMaterial({ color: '#9a9d92', flatShading: true }),
    spots.length,
  );
  mesh.name = 'wall-stones';
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3();
  const col = new THREE.Color();
  spots.forEach((spot, idx) => {
    q.setFromAxisAngle(UP, -spot.rot);
    s.set(1, 0.86 + ((spot.i * 7) % 5) * 0.07, 1);
    m.compose(new THREE.Vector3(spot.x, spot.y + 0.34 * s.y, spot.z), q, s);
    mesh.setMatrixAt(idx, m);
    col.set('#9a9d92').offsetHSL(0, 0, (((spot.i * 13) % 7) - 3) * 0.016);
    mesh.setColorAt(idx, col);
  });
  parent.add(mesh);
}

function makeStoneTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 128;
  c.height = 256;
  const g = c.getContext('2d')!;
  g.fillStyle = '#23262e';
  g.fillRect(0, 0, 128, 256);
  g.fillStyle = '#f7931a';
  g.font = 'bold 40px sans-serif';
  g.textAlign = 'center';
  g.shadowColor = '#f7931a';
  g.shadowBlur = 14;
  g.fillText('600', 64, 78);
  g.font = 'bold 26px sans-serif';
  g.fillText('000', 64, 128);
  g.fillText('000', 64, 172);
  g.fillText('000', 64, 216);
  return new THREE.CanvasTexture(c);
}

function buildCompound(parent: THREE.Object3D, at: THREE.Vector3): {
  bonfires: THREE.Vector3[]; flames: THREE.Sprite[]; fireLight: THREE.PointLight;
  stoneRing: THREE.Mesh; embers: THREE.Sprite[];
} {
  const group = new THREE.Group();
  const baseY = heightAt(at.x, at.z);

  // Ring of standing stones.
  const stoneMat = new THREE.MeshLambertMaterial({ color: '#4a5166', flatShading: true });
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * Math.PI * 2;
    const stone = new THREE.Mesh(new THREE.BoxGeometry(0.9, 2.4 + (i % 3) * 0.5, 0.6), stoneMat);
    stone.position.set(Math.cos(a) * 7.5, 1.1, Math.sin(a) * 7.5);
    stone.rotation.y = -a + 0.2;
    stone.rotation.z = ((i % 5) - 2) * 0.04;
    group.add(stone);
  }

  // Sacred stone monolith.
  const monolith = new THREE.Mesh(
    new THREE.BoxGeometry(1.7, 4.4, 1.1),
    [
      stoneMat, stoneMat,
      stoneMat, stoneMat,
      new THREE.MeshBasicMaterial({ map: makeStoneTexture() }),
      stoneMat,
    ],
  );
  monolith.position.y = 2.1;
  group.add(monolith);

  // Bonfires: crossed charred logs, a stone/coal bed and two flame layers.
  // The old cone-plus-sprite read as a traffic marker beside the ritual; these
  // keep the same tiny footprint but give the fire a hot core and real base.
  const outerFlameTex = makeFlameTexture(
    'rgba(255,205,74,0.96)', 'rgba(255,112,18,0.9)', 'rgba(215,42,6,0.04)',
  );
  const innerFlameTex = makeFlameTexture(
    'rgba(255,255,215,0.98)', 'rgba(255,206,82,0.94)', 'rgba(255,118,12,0.02)',
  );
  const bonfires: THREE.Vector3[] = [];
  const flames: THREE.Sprite[] = [];
  const fireSites: Array<[number, number]> = [[-4.5, 4], [4.8, -3.5]];
  const charred = new THREE.MeshLambertMaterial({ color: '#312218', flatShading: true });
  const coal = new THREE.MeshLambertMaterial({ color: '#242126', emissive: '#351308', flatShading: true });
  const hotCore = new THREE.MeshBasicMaterial({ color: '#ff7a18' });
  const logGeo = new THREE.CylinderGeometry(0.16, 0.2, 1.85, 6);
  const up = new THREE.Vector3(0, 1, 0);
  const fireStones = new THREE.InstancedMesh(new THREE.DodecahedronGeometry(0.27, 0), coal, fireSites.length * 8);
  const dummy = new THREE.Object3D();
  let stoneIndex = 0;
  for (let site = 0; site < fireSites.length; site++) {
    const [bx, bz] = fireSites[site];
    for (let j = 0; j < 8; j++) {
      const a = j / 8 * Math.PI * 2;
      dummy.position.set(bx + Math.cos(a) * 0.95, 0.2, bz + Math.sin(a) * 0.95);
      dummy.rotation.set(j * 0.3, a, j * 0.17);
      dummy.scale.setScalar(0.78 + (j % 3) * 0.09);
      dummy.updateMatrix();
      fireStones.setMatrixAt(stoneIndex++, dummy.matrix);
    }
    for (let j = 0; j < 3; j++) {
      const a = j / 3 * Math.PI;
      const log = new THREE.Mesh(logGeo, charred);
      log.position.set(bx, 0.38 + j * 0.035, bz);
      log.quaternion.setFromUnitVectors(up, new THREE.Vector3(Math.cos(a), 0.12, Math.sin(a)).normalize());
      group.add(log);
    }
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.56, 0), hotCore);
    core.position.set(bx, 0.72, bz);
    core.scale.y = 1.35;
    group.add(core);
    for (const [layer, texture, baseX, baseY, y] of [
      [0, outerFlameTex, 2.1, 3.2, 1.92],
      [1, innerFlameTex, 0.92, 2.05, 1.52],
    ] as const) {
      const flame = new THREE.Sprite(new THREE.SpriteMaterial({
        map: texture,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        transparent: true,
      }));
      flame.scale.set(baseX, baseY, 1);
      flame.position.set(bx + (layer ? 0.08 : 0), y, bz);
      flame.userData = { baseX, baseY, phase: site * 2.4 + layer * 1.15, layer };
      group.add(flame);
      flames.push(flame);
    }
    bonfires.push(new THREE.Vector3(at.x + bx, baseY, at.z + bz));
  }
  fireStones.instanceMatrix.needsUpdate = true;
  group.add(fireStones);
  const fireLight = new THREE.PointLight('#ff8c2a', 14, 34, 1.8);
  fireLight.position.set(0, 2.4, 0);
  group.add(fireLight);

  // The circle itself glows: brand-orange ring inside the standing stones,
  // a faint floor wash, and embers drifting up from the sacred ground.
  const stoneRing = new THREE.Mesh(
    new THREE.RingGeometry(6.6, 8.2, 48),
    new THREE.MeshBasicMaterial({
      color: '#f7931a',
      transparent: true,
      opacity: 0.24,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    }),
  );
  stoneRing.rotation.x = -Math.PI / 2;
  stoneRing.position.y = 0.09;
  group.add(stoneRing);
  const wash = new THREE.Mesh(
    new THREE.CircleGeometry(6.6, 40),
    new THREE.MeshBasicMaterial({
      color: '#f7931a',
      transparent: true,
      opacity: 0.05,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }),
  );
  wash.rotation.x = -Math.PI / 2;
  wash.position.y = 0.08;
  group.add(wash);
  const emberTex = makeGlowTexture('rgba(255,190,90,0.9)', 'rgba(247,110,20,0)');
  const embers: THREE.Sprite[] = [];
  for (let i = 0; i < 12; i++) {
    const ember = new THREE.Sprite(new THREE.SpriteMaterial({
      map: emberTex,
      transparent: true,
      opacity: 0,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    }));
    const s = 0.35 + (i % 4) * 0.14;
    ember.scale.set(s, s, 1);
    ember.userData = {
      r: 1.6 + ((i * 7) % 5),
      a: (i / 12) * Math.PI * 2,
      speed: 0.09 + (i % 3) * 0.045,
      phase: i / 12,
      size: s,
    };
    group.add(ember);
    embers.push(ember);
  }

  group.position.set(at.x, baseY, at.z);
  parent.add(group);
  return { bonfires, flames, fireLight, stoneRing, embers };
}

function buildWater(parent: THREE.Object3D, lake: MapSpec['lake']): { water: THREE.Mesh; waterBase: Float32Array; moonGlints: THREE.Mesh[] } {
  // Ring grid (not a fan) so the surface has interior vertices to wave.
  const geo = new THREE.RingGeometry(0.02, lake.r, 56, 10);
  const water = new THREE.Mesh(
    geo,
    new THREE.MeshPhongMaterial({
      color: '#1a3050',
      emissive: '#0a1626',
      specular: '#cfe0ff',
      shininess: 220,
      transparent: true,
      opacity: 0.9,
      dithering: true,
    }),
  );
  water.rotation.x = -Math.PI / 2;
  water.position.set(lake.x, lake.level, lake.z);
  parent.add(water);
  const waterBase = new Float32Array(geo.attributes.position.array);

  // Moon glitter: a path of shimmering dashes laid toward the moon's azimuth.
  // Each flickers out of phase in updateAmbient — cheap sea-sparkle.
  const moonAngle = Math.atan2(-330 - lake.z, -190 - lake.x);
  const glitter = new THREE.Group();
  glitter.position.set(lake.x, lake.level + 0.07, lake.z);
  glitter.rotation.y = -moonAngle;
  const moonGlints: THREE.Mesh[] = [];
  for (let i = 0; i < 9; i++) {
    const glint = new THREE.Mesh(
      new THREE.PlaneGeometry(0.45 + (i % 3) * 0.18, 1.3 + ((i * 5) % 4) * 0.42),
      new THREE.MeshBasicMaterial({
        color: '#ffffff',
        transparent: true,
        opacity: 0,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    glint.rotation.x = -Math.PI / 2;
    // Biased toward the moon side so the trail reads as its glade.
    glint.position.set(-3.2 + i * 1.55 + Math.sin(i * 9.7) * 0.5, 0, Math.sin(i * 4.3) * 1.2);
    glint.userData = { base: 0.34 - Math.abs(i - 4) * 0.03 };
    glitter.add(glint);
    moonGlints.push(glint);
  }
  parent.add(glitter);

  // Reeds around the shore.
  const reedGeo = new THREE.CylinderGeometry(0.03, 0.05, 1.1, 4);
  const reedMat = new THREE.MeshLambertMaterial({ color: '#3f5a33', flatShading: true });
  const reeds = new THREE.InstancedMesh(reedGeo, reedMat, 34);
  reeds.name = 'reeds';
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3(1, 1, 1);
  for (let i = 0; i < 34; i++) {
    const a = (i / 34) * Math.PI * 2 + Math.sin(i * 7.3) * 0.3;
    const r = lake.r + 0.4 + Math.sin(i * 3.1) * 1.4;
    const x = lake.x + Math.cos(a) * r;
    const z = lake.z + Math.sin(a) * r;
    const y = heightAt(x, z);
    if (y < lake.level - 0.4) continue; // too deep, skip
    q.setFromEuler(new THREE.Euler(Math.sin(i) * 0.12, i, Math.cos(i * 2) * 0.12));
    s.setScalar(0.8 + (i % 5) / 8);
    m.compose(new THREE.Vector3(x, y + 0.5, z), q, s);
    reeds.setMatrixAt(i, m);
  }
  parent.add(reeds);
  return { water, waterBase, moonGlints };
}

function buildRain(scene: THREE.Scene): THREE.Points {
  const count = 720;
  const pos = new Float32Array(count * 3);
  const scale = new Float32Array(count);
  for (let i = 0; i < count; i++) {
    pos[i * 3] = (Math.random() - 0.5) * 72;
    pos[i * 3 + 1] = Math.random() * 26;
    pos[i * 3 + 2] = (Math.random() - 0.5) * 72;
    scale[i] = 0.65 + Math.random() * 0.7;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  geo.setAttribute('aWeatherScale', new THREE.BufferAttribute(scale, 1));
  // A stock PointsMaterial draws every drop as a solid camera-facing square.
  // Close particles therefore became huge blue tiles on a phone and hid the
  // fight. Keep the single draw call, but shape each point into a narrow,
  // depth-capped streak (or a soft round flake in snow lands).
  const mat = new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    toneMapped: false,
    uniforms: {
      uColor: { value: new THREE.Color('#9fb4dd') },
      uOpacity: { value: 0.55 },
      uSnow: { value: 0 },
      uPointScale: { value: 9.5 },
    },
    vertexShader: `
      attribute float aWeatherScale;
      uniform float uPointScale;
      varying float vWeatherAlpha;
      void main() {
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        gl_Position = projectionMatrix * mvPosition;
        float depth = max(1.0, -mvPosition.z);
        gl_PointSize = clamp(uPointScale * aWeatherScale * (34.0 / depth), 3.0, 13.0);
        vWeatherAlpha = 0.65 + aWeatherScale * 0.25;
      }
    `,
    fragmentShader: `
      uniform vec3 uColor;
      uniform float uOpacity;
      uniform float uSnow;
      varying float vWeatherAlpha;
      void main() {
        vec2 p = gl_PointCoord - vec2(0.5);
        float flake = 1.0 - smoothstep(0.28, 0.5, length(p));
        float slant = abs(p.x + p.y * 0.20);
        float streak = (1.0 - smoothstep(0.045, 0.13, slant))
          * (1.0 - smoothstep(0.36, 0.5, abs(p.y)));
        streak *= 0.45 + gl_PointCoord.y * 0.55;
        float shape = mix(streak, flake, uSnow);
        if (shape < 0.01) discard;
        gl_FragColor = vec4(uColor, shape * uOpacity * vWeatherAlpha);
      }
    `,
  });
  const rain = new THREE.Points(geo, mat);
  rain.visible = false;
  rain.frustumCulled = false;
  scene.add(rain);
  return rain;
}

function makeJackFaceTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 64;
  c.height = 64;
  const g = c.getContext('2d')!;
  g.clearRect(0, 0, 64, 64);
  g.fillStyle = '#ffdf6a';
  g.shadowColor = '#ffb347';
  g.shadowBlur = 6;
  // Triangle eyes.
  g.beginPath(); g.moveTo(14, 26); g.lineTo(24, 26); g.lineTo(19, 16); g.closePath(); g.fill();
  g.beginPath(); g.moveTo(40, 26); g.lineTo(50, 26); g.lineTo(45, 16); g.closePath(); g.fill();
  // Jagged mouth.
  g.beginPath();
  g.moveTo(12, 42);
  for (let i = 0; i <= 8; i++) g.lineTo(12 + i * 5, 42 + (i % 2 ? 8 : 0));
  g.lineTo(52, 50); g.lineTo(12, 50);
  g.closePath();
  g.fill();
  return new THREE.CanvasTexture(c);
}

function buildPumpkins(parent: THREE.Object3D, at: THREE.Vector3): void {
  const count = 48;
  const pumpkinGeo = new THREE.SphereGeometry(0.42, 9, 7);
  const pumpkinMat = new THREE.MeshLambertMaterial({ color: '#ffffff', flatShading: true });
  const patch = new THREE.InstancedMesh(pumpkinGeo, pumpkinMat, count);
  patch.name = 'pumpkins';
  const stalkGeo = new THREE.ConeGeometry(0.07, 0.28, 5);
  const stalkMat = new THREE.MeshLambertMaterial({ color: '#4a6030', flatShading: true });
  const stalks = new THREE.InstancedMesh(stalkGeo, stalkMat, count);
  stalks.name = 'pumpkin-stalks';
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  const s = new THREE.Vector3();
  const col = new THREE.Color();
  const spots: THREE.Vector3[] = [];
  let seed = 424242;
  const rand = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let i = 0; i < count; i++) {
    const x = at.x + (rand() - 0.5) * 19;
    const z = at.z + (rand() - 0.5) * 14;
    const y = heightAt(x, z);
    const scale = 0.75 + rand() * 0.65;
    spots.push(new THREE.Vector3(x, y, z));
    q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), rand() * Math.PI * 2);
    s.set(scale, scale * 0.78, scale);
    m.compose(new THREE.Vector3(x, y + 0.3 * scale, z), q, s);
    patch.setMatrixAt(i, m);
    col.set('#e87c1e').offsetHSL((rand() - 0.5) * 0.03, 0, (rand() - 0.5) * 0.1);
    patch.setColorAt(i, col);
    m.compose(new THREE.Vector3(x, y + 0.62 * scale, z), q, new THREE.Vector3(1, 1, 1));
    stalks.setMatrixAt(i, m);
  }
  parent.add(patch, stalks);

  // A few carved jack-o-lanterns glowing in the dark.
  const faceTex = makeJackFaceTexture();
  const glow = makeGlowTexture('rgba(255,190,90,0.55)', 'rgba(247,120,26,0)');
  for (let i = 0; i < 6; i++) {
    const p = spots[Math.floor((i * 5.5 + 2) % spots.length)];
    const face = new THREE.Mesh(
      new THREE.PlaneGeometry(0.72, 0.72),
      new THREE.MeshBasicMaterial({
        map: faceTex,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    face.position.set(p.x, p.y + 0.34, p.z + 0.47);
    parent.add(face);
    const halo = new THREE.Sprite(new THREE.SpriteMaterial({
      map: glow,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      opacity: 0.7,
    }));
    halo.scale.setScalar(2.3);
    halo.position.set(p.x, p.y + 0.42, p.z);
    parent.add(halo);
  }
}

function buildGraveyard(parent: THREE.Object3D, at: THREE.Vector3): void {
  const group = new THREE.Group();
  const stoneMat = new THREE.MeshLambertMaterial({ color: '#5c6274', flatShading: true });
  let seed = 31337;
  const rand = () => { seed = (seed * 16807) % 2147483647; return seed / 2147483647; };
  for (let i = 0; i < 9; i++) {
    const headstone = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.9, 0.14), stoneMat);
    const lx = (i % 3) * 2.2 - 2.2 + (rand() - 0.5) * 0.7;
    const lz = Math.floor(i / 3) * 2 - 2 + (rand() - 0.5) * 0.7;
    const y = heightAt(at.x + lx, at.z + lz) - heightAt(at.x, at.z);
    headstone.position.set(lx, y + 0.4, lz);
    headstone.rotation.set((rand() - 0.5) * 0.24, rand() * 0.5, (rand() - 0.5) * 0.24);
    group.add(headstone);
  }
  // Two crosses.
  for (const [cx, cz] of [[3.2, 0.4], [-3.4, -1.2]]) {
    const cross = new THREE.Group();
    const v = new THREE.Mesh(new THREE.BoxGeometry(0.16, 1.15, 0.16), stoneMat);
    const h = new THREE.Mesh(new THREE.BoxGeometry(0.62, 0.16, 0.16), stoneMat);
    v.position.y = 0.55;
    h.position.y = 0.78;
    cross.add(v, h);
    const y = heightAt(at.x + cx, at.z + cz) - heightAt(at.x, at.z);
    cross.position.set(cx, y, cz);
    cross.rotation.z = (Math.random() - 0.5) * 0.18;
    group.add(cross);
  }
  // A dead tree looming over the graves.
  group.add(makeDeadTree(1.3, 0, -3.4));
  group.position.set(at.x, heightAt(at.x, at.z), at.z);
  group.rotation.y = 0.4;
  parent.add(group);
}

function makeDeadTree(scale: number, x: number, z: number): THREE.Group {
  const tree = new THREE.Group();
  const mat = new THREE.MeshLambertMaterial({ color: '#241d18', flatShading: true });
  const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.2, 2.6, 5), mat);
  trunk.position.y = 1.3;
  tree.add(trunk);
  const branches: [number, number, number, number][] = [
    [1.8, 0.5, 0.9, 1.1],
    [2.2, -0.6, 1.1, 0.8],
    [1.4, 0.2, -0.9, 0.9],
    [2.5, 0.7, -0.4, 0.6],
  ];
  for (const [by, bz, brot, blen] of branches) {
    const branch = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.07, blen, 4), mat);
    branch.position.set(0, by, 0);
    branch.rotation.set(brot, bz, 0.7);
    branch.translateY(blen / 2);
    tree.add(branch);
  }
  tree.scale.setScalar(scale);
  tree.position.set(x, 0, z);
  tree.rotation.y = Math.random() * Math.PI * 2;
  return tree;
}

function buildMist(scene: THREE.Scene): THREE.Sprite[] {
  const tex = makeGlowTexture('rgba(150,160,205,0.16)', 'rgba(150,160,205,0)');
  const mist: THREE.Sprite[] = [];
  for (let i = 0; i < 12; i++) {
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
      map: tex,
      transparent: true,
      opacity: 0.5,
      depthWrite: false,
      fog: false,
    }));
    const a = (i / 12) * Math.PI * 2;
    const r = 16 + (i % 5) * 16;
    sprite.position.set(Math.cos(a) * r, 1.1 + (i % 3) * 0.5, Math.sin(a) * r);
    sprite.scale.set(16 + (i % 3) * 8, 5.5, 1);
    scene.add(sprite);
    mist.push(sprite);
  }
  return mist;
}

function buildBats(scene: THREE.Scene): THREE.Mesh[] {
  // A bat: two triangles meeting at the body.
  const geo = new THREE.BufferGeometry();
  const verts = new Float32Array([
    0, 0, 0, -0.9, 0.25, 0, -0.45, -0.1, 0,
    0, 0, 0, 0.45, -0.1, 0, 0.9, 0.25, 0,
  ]);
  geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
  const mat = new THREE.MeshBasicMaterial({ color: '#05060a', side: THREE.DoubleSide, fog: false });
  const bats: THREE.Mesh[] = [];
  for (let i = 0; i < 10; i++) {
    const bat = new THREE.Mesh(geo, mat);
    bat.userData = {
      radius: 30 + Math.random() * 45,
      height: 20 + Math.random() * 16,
      speed: 0.12 + Math.random() * 0.12,
      phase: Math.random() * Math.PI * 2,
      flap: 5 + Math.random() * 4,
    };
    bat.scale.setScalar(1.6);
    scene.add(bat);
    bats.push(bat);
  }
  return bats;
}

export function initWorld(canvas: HTMLCanvasElement): World {
  const coarse = matchMedia('(pointer: coarse)').matches;
  const nativeDpr = window.devicePixelRatio || 1;
  // Retina phones pay for every fragment twice over if we combine a large
  // backing buffer with MSAA. At arm's length 1.5x still resolves the game's
  // chunky silhouettes cleanly, while cutting 26% of the pixels versus 1.75x.
  // Keep MSAA for genuinely 1x touch screens, where jaggies are more visible
  // and the backing buffer itself is cheap.
  const dpr = Math.min(nativeDpr, coarse ? 1.5 : 2);
  const renderer = new THREE.WebGLRenderer({
    canvas,
    antialias: coarse ? nativeDpr <= 1 : dpr < 1.8,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(dpr);
  renderer.setSize(window.innerWidth, window.innerHeight, false);
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  const scene = new THREE.Scene();
  scene.fog = new THREE.Fog('#141030', 70, 250);

  const camera = new THREE.PerspectiveCamera(55, window.innerWidth / window.innerHeight, 0.1, 900);
  camera.position.set(0, 16, 20);

  const hemi = new THREE.HemisphereLight('#6b7cc4', '#26382a', 2.15);
  scene.add(hemi);
  const moonlight = new THREE.DirectionalLight('#a8bcf5', 1.55);
  moonlight.position.set(-140, 120, -240);
  scene.add(moonlight);

  const { moonMat, moonHaloMat } = buildSky(scene);

  // Every land is its own map: a set-piece kit built against ITS heightfield
  // (activateMapData switches heightAt), then shown or hidden per biome. The
  // shared terrain/road sheets morph instead of being rebuilt.
  //
  // Kits are built ON DEMAND. With three lands, building all of them up front
  // cost one hitch at boot and was fine; with seven it is most of the launch
  // budget spent on lands the player may never visit this run. `warmBiome`
  // lets the game pay that cost somewhere it cannot be felt — during the
  // ritual, seconds before the departure that needs it.
  interface BiomeKit {
    group: THREE.Group;
    corn: CornSystem;
    stoneAt: THREE.Vector3;
    chapelAt: THREE.Vector3;
    compound: ReturnType<typeof buildCompound>;
    waterKit: ReturnType<typeof buildWater>;
    props: ReturnType<typeof buildProps>;
  }

  function buildBiomeKit(id: BiomeId): BiomeKit {
    activateMapData(id);
    const map = MAPS[id];
    const group = new THREE.Group();
    group.visible = false;
    scene.add(group);

    const compoundAt = new THREE.Vector3(map.compoundAt[0], 0, map.compoundAt[1]);
    const chapelAt = new THREE.Vector3(map.chapelAt[0], 0, map.chapelAt[1]);
    const farmVecs = map.farms.map(([x, z]) => new THREE.Vector3(x, 0, z));
    const pumpkinVecs = map.pumpkins.map(([x, z]) => new THREE.Vector3(x, 0, z));
    const graveVecs = map.graveyards.map(([x, z]) => new THREE.Vector3(x, 0, z));
    const terraceVecs = (map.terraces ?? []).flatMap((row) =>
      Array.from({ length: row.n }, (_, i) => new THREE.Vector3(
        row.cx + Math.cos(row.rot) * i * 3.4,
        0,
        row.cz + Math.sin(row.rot) * i * 3.4,
      )));

    for (const at of farmVecs) buildFarm(group, at, id);
    if (map.donkeyFarm) {
      buildDonkeyFarm(group, new THREE.Vector3(map.donkeyFarm[0], 0, map.donkeyFarm[1]));
    }
    const compound = buildCompound(group, compoundAt);
    for (const at of pumpkinVecs) buildPumpkins(group, at);
    for (const at of graveVecs) buildGraveyard(group, at);
    if (map.streets) buildStreets(group, map.streets);
    if (map.terraces) buildTerraces(group, map.terraces);
    if (map.walls) buildWalls(group, map.walls);
    buildChapel(group, chapelAt);
    chapelAt.y = heightAt(chapelAt.x, chapelAt.z);
    for (const [scale, x, z] of map.deadTrees) {
      const tree = makeDeadTree(scale, x, z);
      tree.position.y = heightAt(x, z);
      group.add(tree);
    }
    const corn = new CornSystem(map.cornFields);
    group.add(corn.mesh);
    const waterKit = buildWater(group, map.lake);

    const avoid = [compoundAt, chapelAt, new THREE.Vector3(0, 0, 0),
      new THREE.Vector3(map.lake.x, 0, map.lake.z), ...farmVecs, ...pumpkinVecs, ...graveVecs,
      ...(map.donkeyFarm ? [new THREE.Vector3(map.donkeyFarm[0], 0, map.donkeyFarm[1])] : []),
      ...terraceVecs, ...map.cornFields.map((f) => new THREE.Vector3(f.cx, 0, f.cz)),
      ...map.deadTrees.map(([, x, z]) => new THREE.Vector3(x, 0, z)),
      ...(map.pylons?.flat().map(([x, z]) => new THREE.Vector3(x, 0, z)) ?? [])];
    const props = buildProps(group, avoid, map.trees, map.rocks);

    const stoneAt = new THREE.Vector3(compoundAt.x, heightAt(compoundAt.x, compoundAt.z), compoundAt.z);
    return { group, corn, stoneAt, chapelAt, compound, waterKit, props };
  }

  const kits: Partial<Record<BiomeId, BiomeKit>> = {};

  /** The kit for a land, built the first time it is asked for. Leaves that
   *  land's map data active — every caller either wants that or restores it. */
  function kitFor(id: BiomeId): BiomeKit {
    const existing = kits[id];
    if (existing) return existing;
    const built = buildBiomeKit(id);
    kits[id] = built;
    return built;
  }

  // Shared sheets sample the ACTIVE map — valley from here on.
  const valleyKit = kitFor('valley');
  activateMapData('valley');
  const terrainGeo = buildTerrain(scene);
  const roadGeo = buildRoad(scene);
  valleyKit.group.visible = true;

  const rain = buildRain(scene);
  const mist = buildMist(scene);
  const bats = buildBats(scene);

  const { corn, stoneAt, chapelAt } = valleyKit;
  const { bonfires, flames, fireLight, stoneRing, embers } = valleyKit.compound;
  const { water, waterBase, moonGlints } = valleyKit.waterKit;
  const world: World = {
    scene, camera, renderer, heightAt, bonfires, flames, fireLight, corn, mist, bats, stoneAt, chapelAt,
    stoneRing, embers, moonGlints,
    water, waterBase, rain, hemi, sun: moonlight, moonMat, moonHaloMat,
    biome: 'valley',
    biomeLabel: BIOMES.valley.label,
    waterAmp: BIOMES.valley.waterAmp,
    precip: BIOMES.valley.precip,
    fogColor: new THREE.Color(BIOMES.valley.fog),
    moonColor: new THREE.Color(BIOMES.valley.moon),
    haloColor: new THREE.Color(BIOMES.valley.halo),
    hemiSkyColor: new THREE.Color(BIOMES.valley.hemiSky),
    propClearAt(x: number, z: number, radius: number): boolean {
      const props = kitFor(world.biome).props;
      return props.treeSpots.every((spot) => Math.hypot(x - spot.x, z - spot.z) > radius + 1.6) &&
        props.rockSpots.every((spot) => Math.hypot(x - spot.x, z - spot.z) > radius + 1.1) &&
        props.clearSpots.every((spot) => Math.hypot(x - spot.x, z - spot.z) > radius + 6.8);
    },
    warmBiome(id: BiomeId): void {
      if (kits[id]) return;
      const current = world.biome;
      kitFor(id);              // builds against id's heightfield...
      activateMapData(current); // ...so put the live map back before returning
    },
    applyBiome(id: BiomeId): void {
      const p = BIOMES[id];
      const switching = id !== world.biome;
      if (switching) {
        // A different LAND, not just a different wash: swap the whole map.
        kitFor(world.biome).group.visible = false;
        activateMapData(id);
        world.biome = id;
      }
      // After activateMapData, so a first visit builds against the right map.
      const kit = kitFor(id);
      if (switching) {
        kit.group.visible = true;
        morphTerrain(terrainGeo);
        layRoad(roadGeo);
        // The game reads these off the world object live — repoint them all.
        world.corn = kit.corn;
        world.stoneAt = kit.stoneAt;
        world.chapelAt = kit.chapelAt;
        world.bonfires = kit.compound.bonfires;
        world.flames = kit.compound.flames;
        world.fireLight = kit.compound.fireLight;
        world.stoneRing = kit.compound.stoneRing;
        world.embers = kit.compound.embers;
        world.water = kit.waterKit.water;
        world.waterBase = kit.waterKit.waterBase;
        world.moonGlints = kit.waterKit.moonGlints;
      }
      world.biomeLabel = p.label;
      world.waterAmp = p.waterAmp;
      world.precip = p.precip;
      world.fogColor.set(p.fog);
      world.moonColor.set(p.moon);
      world.haloColor.set(p.halo);
      world.hemiSkyColor.set(p.hemiSky);
      paintTerrain(terrainGeo, p);
      paintRoad(roadGeo, p);
      paintCanopies(kit.props.canopies, kit.props.treeCount, p.canopy);
      kit.corn.retint(p.corn);
      kit.props.rockMat.color.set(p.rockProp);
      hemi.color.set(p.hemiSky);
      hemi.groundColor.set(p.hemiGround);
      moonlight.color.set(p.moonlight);
      moonMat.color.set(p.moon);
      moonHaloMat.color.set(p.halo);
      (scene.fog as THREE.Fog).color.set(p.fog);
      const wm = kit.waterKit.water.material as THREE.MeshPhongMaterial;
      wm.color.set(p.water);
      wm.emissive.set(p.waterEmissive);
      wm.opacity = p.waterOpacity;
      wm.shininess = p.waterShine;
      const rm = rain.material as THREE.ShaderMaterial;
      (rm.uniforms.uColor.value as THREE.Color).set(p.precipColor);
      rm.uniforms.uSnow.value = p.precip === 'snow' ? 1 : 0;
      rm.uniforms.uPointScale.value = p.precip === 'snow' ? 7.5 : 9.5;
      for (const sprite of mist) sprite.material.color.set(p.mistTint);
      for (const glint of kit.waterKit.moonGlints) (glint.material as THREE.MeshBasicMaterial).color.set(p.halo);
    },
  };
  return world;
}
